import { createDashboardPanelHistory } from './components/routing/dashboardPanelHistory';
import {
  searchReportingFetch as fetch,
  searchReportingLegacyUrl,
} from './lib/flags/search-reporting';
// Update imports - remove old imports and add new ones
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import {
  Switch,
  Route,
  useLocation,
  Redirect,
  useParams,
  Router,
  useHistory,
} from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setReadOnly } from './redux/slices/readOnly';
import { usePostHog } from 'posthog-js/react';
import { motion, useReducedMotion } from 'framer-motion';
import '../../donation-elements/src/shared-styles/index.css';
// CON-48: preserve the shared stylesheet's former html-reset position for this
// app (v3's cascade model, CON-165) while the donor apps leave root line-height
// to preflight.
import './css/donor-html-line-height.css';
// CON-46: the shared `body` defaults, split out of the stylesheet above and
// imported UNLAYERED here. The dashboard runs Tailwind v4 unlayered, in v3's
// cascade model (CON-165), so this is exactly the cascade position the rule has
// always had here and the dashboard's body typeface is unchanged. (Until CON-164
// the reason was also v3's PostCSS plugin rejecting an `@layer base` block.) Nothing else in the dashboard declares
// a body font-family, so dropping this import would silently re-point the whole
// admin UI at Tailwind's preflight default.
//
// Imported by the DECLARED workspace dep name, not by a relative path into
// another package's src tree (CLAUDE.md "Package Boundary Rules"). `dashboard`
// already declares `donation-elements: workspace:*`, so this is the contract-
// visible form; the relative import above it predates that rule and is left
// alone rather than re-resolved as a drive-by.
import 'donation-elements/src/shared-styles/base.css';

// CON-2 part 2: @charitystack/design-tokens/dashboard.css — a plain CSS
// :root {} block of --cs-* custom properties, no Tailwind directives. A
// real JS side-effect import (not a Sass @import inside style.scss) is
// required here: Dart Sass treats an @import of an external *.css path as a
// literal pass-through `@import url(...)` rule rather than inlining it at
// compile time, so the --cs-* declarations would never actually reach this
// bundle's cascade. Importing it as a JS module instead routes it through
// Vite's normal CSS pipeline (dashboard's own v3 postcss.config.js:
// tailwindcss + autoprefixer), which is a no-op on a directive-free :root
// block. Makes the tailwind.config.js preset's var(--cs-*) reads (added in
// the same commit) resolve; does not itself change any rendered value.
import '@charitystack/design-tokens/dashboard.css';
// CON-42: the donor value set, applied to a SUBTREE rather than :root. The form
// previewer mounts donation-elements components in this document, and without this
// they resolve every --cs-* against dashboard.css — see decisions.md D-previewer-scope.
// Never import donor.css here: that would put the donor values on the dashboard's own
// root and repaint the whole app.
import '@charitystack/design-tokens/donor-scope.css';
// The contact-portal previews mount the real shared portal experience inside
// the dashboard document. Keep its values subtree-scoped for the same reason:
// importing portal.css itself would repaint dashboard :root.
import '@charitystack/design-tokens/portal-scope.css';

// Import dashboard's own CSS
import './css/style.scss';

// Clerk imports
import { useAuth, useUser, useClerk, useOrganization, useSession } from '@clerk/clerk-react';

import { focusHandling } from 'cruip-js-toolkit';
import './charts/ChartjsConfig';

// Email Blasts + Email Templates removed (PE-1671) — pages, routes, nav all
// deleted. See the PE-1671 "what was removed" comment for the restore recipe.
import NotFound from './partials/404';
import Loading from './partials/loading';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import SignupFlow from './pages/SignupFlow';
import RequestAccess from './pages/RequestAccess';
import MaintenancePage from './pages/MaintenancePage';
import Sidebar from './partials/Sidebar';
import Header from './partials/Header';
import DashboardAssistantRuntime from './components/panels/DashboardAssistantRuntime';
import DashboardAssistantBottomBar from './components/panels/DashboardAssistantBottomBar';
import { SetupChecklistPreferencesProvider } from './partials/onboarding/checklist/SetupChecklistPreferences';
import { paymentOrganizationKey } from './utils/paymentReadiness';
import { DASHBOARD_ASSISTANT_ENABLED } from './components/panels/DashboardAssistantProvider';
import { useDashboardAssistantDock } from './contexts/DashboardAssistantDockContext';
import { chatPanelLayoutTransition } from './components/panels/DashboardAssistantChatDock/lib/panelLayout';
import { OnboardingDriverProvider } from './partials/onboarding/GuidedFormCreation/OnboardingDriverContext';
import DashboardOnboardingChrome from './partials/onboarding/DashboardOnboardingChrome';
import {
  StripeSessionProvider,
  useStripeSessionLifecycle,
} from './partials/onboarding/StripeSessionContext';
import { useSignupOnboardingGate } from './hooks/useSignupOnboardingGate';
import { useCompleteOrganizationList } from './hooks/useCompleteOrganizationList';
import { useDashboardOnboardingUi } from './hooks/useDashboardOnboardingUi';
import { useMerchantHydration } from './hooks/useMerchantHydration';
import { useScopedMerchant } from './hooks/useScopedMerchant';
import {
  ACTIVATION_NAV_BLOCK_PATH,
  isCreateOrganizationIntent,
  isOnboardingRoutePath,
  pathnameTouchesOnboardingForProductlane,
} from './utils/onboardingPaths';
import { clearPlanIntent } from './partials/onboarding/shared/planIntent';
import {
  isWithinProductlaneSessionWindow,
  recordProductlaneSessionOnce,
  configureProductlaneWidget,
} from './utils/productlaneVisibility';
import AuthMarketingSplitLayout, {
  AuthMarketingLoadingState,
} from './components/AuthMarketingShell';
import CharityStackLogo from './assets/CharityStack Text.png';
import { toast } from 'sonner';
import { Sentry } from '@charitystack/shared';
import { MergeProvider, useMerge } from './contexts/MergeContext';
import { DashboardRoutePanelProvider } from './contexts/DashboardRoutePanelContext';
import DashboardRouteFallback from './components/loading/DashboardRouteFallback';
import DashboardListRouteCache from './components/routing/DashboardListRouteCache';
import { EntitlementsProvider, useEntitlements } from './contexts/EntitlementsContext';
import { UpgradeLock } from './components/entitlements/UpgradeLock';
import { PermissionPlaceholder } from './components/permissions/PermissionPlaceholder';
import MarketingEngagement from './partials/marketing/engagement/MarketingEngagement';
import ElementsEngagement from './partials/elements/engagement/ElementsEngagement';
import { trackMarketing, MARKETING_EVENTS } from './utils/marketingAnalytics';
import {
  DASHBOARD_PLG_EVENTS,
  dashboardWorkspaceFromPath,
  dashboardWorkspaceViewKey,
  trackDashboardPlg,
} from './utils/dashboardPlgAnalytics';
import {
  ELEMENT_FEATURE_KEYS,
  MARKETING_FEATURE_KEYS,
  FEATURE,
  unlockTierLabel,
} from './utils/entitlements';
import {
  getAuthToken,
  primeTokenCache,
  setClerkGetTokenFn,
  setDevAuthOverride,
  clearDevAuthOverride,
  checkPermission,
  getEffectiveRole,
  getUserPermissions,
  getCustomRole,
} from './utils/auth';
import { buildContactUsMailto } from './utils/contactSales';
import { growPageUrl } from './utils/siteUrls';
import { P, ROLES } from './utils/permissions';
import { safeFetchJSON } from '@charitystack/shared';
import { decodeJwtPayloadSafe, logAuthDiag } from './utils/authDiagnostics';
import { accountsClient } from './lib/trpc/accounts';
import { getMerchantRecordId, getOrganizationInitials } from './utils/merchantIdentity';
import {
  readStripeReviewLink,
  stripeReviewSearch,
  stripeReviewReturnPath,
  stripeReviewMatches,
  stripeReviewNeedsOrganizationSelection,
  STRIPE_REVIEW_PATH,
} from './utils/stripeReviewLink';
import DevStateLab from './dev-state-lab/DevStateLab';
import { applyDevStateOverrides } from './dev-state-lab/applyDevStateOverrides';
import {
  createDevStateLabScope,
  isDevStateLabEnabled,
  useDevStateLabState,
} from './dev-state-lab/devStateStore';
import {
  DASHBOARD_TABS_STATE_EVENT,
  createDashboardLocationFromPath,
  createDashboardFallbackTab,
  getDashboardLocationSignature,
  getDashboardPathFromLocation,
  getEffectiveDashboardTabsState,
  normalizeDashboardTabsState,
  readDashboardTabsState,
  writeDashboardTabsState,
} from './utils/dashboardTabs';
import {
  activateDashboardOrganization,
  getMembershipOrganization,
  getMembershipDisplayName,
  getMembershipOrganizationId,
  syncActiveOrganization,
} from './utils/activeOrganization';
// CON-165: a fallback, not the placement. src/tailwind.css is meant to be
// reached first through payment-methods/Confirmation*.js (see the comment
// there); by the time the bundle gets here it already has been, and this line
// does nothing. It exists so that if those screens are ever code-split, the
// dashboard's Tailwind still ships in the main bundle instead of a lazy chunk.
import './tailwind.css';

// Authenticated route pages are loaded on demand. Keeping Rollup's natural
// dependency graph here avoids the oversized single dashboard chunk that can
// exceed Amplify's final import-analysis limits without introducing brittle
// hand-authored vendor chunks.
const Donations = React.lazy(() => import('./pages/Donations'));
const PaymentWorkspace = React.lazy(() => import('./pages/PaymentWorkspace'));
const Donors = React.lazy(() => import('./pages/Donors'));
const Home = React.lazy(() => import('./pages/Home'));
const ComingSoon = React.lazy(() => import('./pages/ComingSoon'));
const BroadcastsPage = React.lazy(() => import('./pages/marketing/BroadcastsPage'));
const BroadcastDetailPage = React.lazy(() => import('./pages/marketing/BroadcastDetailPage'));
const CampaignDetailsPage = React.lazy(() => import('./pages/marketing/CampaignDetailsPage'));
const NewBroadcastPage = React.lazy(() => import('./pages/marketing/NewBroadcastPage'));
const BroadcastPaymentsPage = React.lazy(() => import('./pages/marketing/BroadcastPaymentsPage'));
const BrandKitPage = React.lazy(() => import('./pages/marketing/BrandKitPage'));
const SegmentsPage = React.lazy(() => import('./pages/marketing/SegmentsPage'));
const SegmentEditPage = React.lazy(() => import('./pages/marketing/SegmentEditPage'));
const NewSegmentPage = React.lazy(() => import('./pages/marketing/NewSegmentPage'));
const Settings = React.lazy(() => import('./pages/Settings'));
// The Import HUB is not lazy-loaded here — PE-1910 moved it into the Settings
// shell, where SettingsContent.js owns its React.lazy import and renders it as
// the `imports` tab.
//
// The WIZARD is deliberately NOT a Settings tab. It is a full-page 4-step
// workspace (SPEC §4) with its own header and 100dvh shell, so it mounts as a
// standalone route below. Lazy so hello-csv (+108 kB gzip) and read-excel-file
// land in their own chunk rather than the dashboard's main bundle.
const ImportWizardPage = React.lazy(() => import('./pages/imports/wizard/ImportWizardPage'));
const Elements = React.lazy(() => import('./pages/Elements'));
const ElementWorkspace = React.lazy(() => import('./pages/ElementWorkspace'));
const Fundraisers = React.lazy(() => import('./pages/Fundraisers'));
const FormWorkspace = React.lazy(() => import('./pages/FormWorkspace'));
const FormSubmissionWorkspace = React.lazy(() => import('./pages/FormSubmissionWorkspace'));
const ContactWorkspace = React.lazy(() => import('./pages/ContactWorkspace'));
const Subscriptions = React.lazy(() => import('./pages/Subscriptions'));
const SubscriptionWorkspace = React.lazy(() => import('./pages/SubscriptionWorkspace'));
const Payouts = React.lazy(() => import('./pages/Payouts'));
const OptIn = React.lazy(() => import('./pages/OptIn'));

// ── iOS Mobile Detection ──────────────────────────────────────────────────────
// Stripe Connect embedded components use cross-origin iframes from connect-js.stripe.com.
// On iOS, all third-party browsers (Chrome, Google App, Firefox, etc.) use WKWebView,
// which Stripe explicitly does not support for embedded components.
// See: https://docs.stripe.com/connect/get-started-connect-embedded-components
// ("Connect embedded components are only supported in web browsers and can't be used
//  in embedded web views inside mobile or desktop applications.")
//
// Even on Mobile Safari, WebKit's ITP (Intelligent Tracking Prevention) can
// interfere with the iframe's account_sessions/claim request, causing 400 errors.
// Sentry: CHARITYSTACK-MONOREPO-AV (42 events, 100% iOS mobile).
const isIOSWebView = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  if (!isIOS) return false;
  // Generic WKWebView omits the "Safari/" token from the UA string entirely.
  // Known in-app browsers (Chrome iOS, Google App, etc.) keep "Safari/" but add their own token.
  // Mobile Safari always includes "Safari/" without any app-specific identifier.
  return (
    !ua.includes('Safari/') ||
    /CriOS|FxiOS|OPiOS|EdgiOS|GSA|FBAN|FBAV|Instagram|Twitter|LinkedIn/.test(ua)
  );
})();

// Determine the base path for redirects (same logic as index.jsx)
// This ensures redirects work correctly both locally (/) and in production (/dashboard)
const ROUTER_BASENAME = window.location.pathname.startsWith('/dashboard') ? '/dashboard/' : '/';

// Auth route paths (centralized to avoid hardcoded repetition)
const AUTH_PATHS = { SIGN_IN: 'signin', SIGN_UP: 'signup', REQUEST_ACCESS: 'request-access' };
const MAX_CACHED_DASHBOARD_TAB_PANELS = 12;

function DashboardRouteCache({ activeLocation, renderRoutes, cacheScope, fallbackHeaderProps }) {
  const activePath = getDashboardPathFromLocation(activeLocation);
  const outerHistory = useHistory();
  const [tabsState, setTabsState] = useState(() => readDashboardTabsState());
  const panelLocations = useRef(new Map());
  const effectiveTabsState = getEffectiveDashboardTabsState(tabsState, activePath);

  useEffect(() => {
    const syncTabsState = (event) => {
      setTabsState(normalizeDashboardTabsState(event?.detail || readDashboardTabsState()));
    };

    syncTabsState();
    window.addEventListener(DASHBOARD_TABS_STATE_EVENT, syncTabsState);
    window.addEventListener('storage', syncTabsState);

    return () => {
      window.removeEventListener(DASHBOARD_TABS_STATE_EVENT, syncTabsState);
      window.removeEventListener('storage', syncTabsState);
    };
  }, []);

  useEffect(() => {
    panelLocations.current = new Map();
    setTabsState(readDashboardTabsState());
  }, [cacheScope]);

  const panels = effectiveTabsState.tabs.slice(-MAX_CACHED_DASHBOARD_TAB_PANELS);
  const hasActivePanel = panels.some((tab) => tab.id === effectiveTabsState.activeTabId);
  const renderedPanels = hasActivePanel
    ? panels
    : [
        ...panels.slice(1 - MAX_CACHED_DASHBOARD_TAB_PANELS),
        createDashboardFallbackTab(activePath),
      ];
  const renderedPanelIds = new Set(renderedPanels.map((tab) => tab.id));

  panelLocations.current.forEach((_, tabId) => {
    if (!renderedPanelIds.has(tabId)) {
      panelLocations.current.delete(tabId);
    }
  });

  return (
    <>
      {renderedPanels.map((tab) => {
        const panelIsActive = tab.id === effectiveTabsState.activeTabId;
        const panelPath = panelIsActive ? activePath : tab.path;
        const cachedPanelLocation = panelLocations.current.get(tab.id);
        let panelLocation = cachedPanelLocation;

        if (panelIsActive) {
          const cachedPathMatchesActivePath =
            cachedPanelLocation &&
            getDashboardPathFromLocation(cachedPanelLocation) === activePath &&
            activeLocation.state == null;
          panelLocation = cachedPathMatchesActivePath ? cachedPanelLocation : activeLocation;
        } else if (!panelLocation || getDashboardPathFromLocation(panelLocation) !== panelPath) {
          panelLocation = createDashboardLocationFromPath(panelPath);
        }

        panelLocations.current.set(tab.id, panelLocation);

        return (
          <DashboardListRouteCache
            key={`${cacheScope || 'default'}:${tab.id}`}
            PanelComponent={DashboardRoutePanel}
            panelId={tab.id}
            isActive={panelIsActive}
            location={panelLocation}
            outerHistory={outerHistory}
            renderRoutes={renderRoutes}
            fallbackHeaderProps={fallbackHeaderProps}
          />
        );
      })}
    </>
  );
}

function DashboardRoutePanel({
  panelId,
  isActive,
  location,
  outerHistory,
  renderRoutes,
  fallbackHeaderProps,
}) {
  const historyController = useRef(null);
  const locationSignature = getDashboardLocationSignature(location);
  const previousLocationSignature = useRef(locationSignature);

  useLayoutEffect(() => () => historyController.current?.dispose(), []);

  if (!historyController.current) {
    historyController.current = createDashboardPanelHistory(outerHistory, location, isActive);
  }

  historyController.current.setSnapshot({
    outerHistory,
    location,
    isActive,
    action: isActive ? outerHistory.action : 'POP',
  });

  useLayoutEffect(() => {
    if (previousLocationSignature.current !== locationSignature) {
      previousLocationSignature.current = locationSignature;
      historyController.current.notify();
    }
  }, [locationSignature]);

  // Inactive tab panels stay MOUNTED (visually hidden via `hidden` +
  // `aria-hidden`) instead of unmounting to `null`. This preserves ALL native
  // React state across a tab switch — unsaved form input, open drawers, scroll,
  // etc. — for every page, not just a subset. `isActivePanel` is still passed as
  // `isActive` (false) so consumers of useDashboardRoutePanelActive() continue to
  // treat a hidden panel as inactive.
  //
  // TRADEOFF (accepted): keeping panels mounted means their background polling
  // keeps running while hidden — e.g. BroadcastDetailPage's 15s status poll —
  // run per cached tab (bounded by MAX_CACHED_DASHBOARD_TAB_PANELS). This is
  // deliberate: preserving unsaved work
  // on every page outweighs the extra background API traffic. Panels are still
  // torn down on tab close / cache eviction beyond the cap / org switch, so the
  // PocketFlows iframe parking (MarketingEmbedFrame, PE-1750) still runs for
  // those real unmounts; a plain tab switch now just leaves the iframe attached.
  return (
    <div
      className={isActive ? 'flex min-h-0 min-w-0 flex-1 flex-col' : 'hidden'}
      aria-hidden={!isActive}
    >
      <Router history={historyController.current.history}>
        <DashboardRoutePanelProvider
          isActivePanel={isActive}
          panelLocation={location}
          panelId={panelId}
        >
          <React.Suspense
            fallback={
              <DashboardRouteFallback
                pathname={location.pathname}
                header={
                  <div className="mx-auto w-full">
                    <Header {...fallbackHeaderProps} />
                  </div>
                }
              />
            }
          >
            {renderRoutes(location)}
          </React.Suspense>
        </DashboardRoutePanelProvider>
      </Router>
    </div>
  );
}

/**
 * Check if a pathname is a public auth route (sign-in, sign-up, request-access).
 * Uses startsWith for signin/signup to match Clerk sub-paths (e.g. /signin/factor-one).
 */
function isAuthRoute(pathname) {
  return (
    pathname.startsWith('/' + AUTH_PATHS.SIGN_IN) ||
    pathname.startsWith(ROUTER_BASENAME + AUTH_PATHS.SIGN_IN) ||
    pathname.startsWith('/' + AUTH_PATHS.SIGN_UP) ||
    pathname.startsWith(ROUTER_BASENAME + AUTH_PATHS.SIGN_UP) ||
    pathname === '/' + AUTH_PATHS.REQUEST_ACCESS ||
    pathname === ROUTER_BASENAME + AUTH_PATHS.REQUEST_ACCESS
  );
}

// Update authentication handling
async function validateId(id) {
  try {
    // Get Clerk auth token
    const jwt = await getAuthToken();

    const response = await fetch(
      `${searchReportingLegacyUrl}/validate/transaction?transactionID=${id}`,
      {
        method: 'GET',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: jwt,
        },
      },
    );

    if (!response.ok) {
      console.error('Network response was not ok');
      return { isValid: false };
    }

    const data = await response.json();
    // The API returns transactionInformation as a one-element array; unwrap it.
    // Fall back to treating a plain object as the transaction (API future-proofing).
    const rawInfo = data.isValid ? data.transactionInformation : null;
    const transactionInformation = Array.isArray(rawInfo)
      ? (rawInfo[0] ?? null)
      : (rawInfo ?? null);
    return {
      isValid: data.isValid && transactionInformation !== null,
      transactionInformation,
    };
  } catch (error) {
    console.error('Error validating transaction:', error);
    return { isValid: false };
  }
}

// Add a new function for validating contacts
async function validateContact(id) {
  try {
    // Get Clerk auth token
    const jwt = await getAuthToken();

    const response = await fetch(`${searchReportingLegacyUrl}/validate/contact?contactID=${id}`, {
        method: 'GET',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: jwt,
        },
    });

    if (!response.ok) {
      console.error('Network response was not ok');
      return { isValid: false };
    }

    const data = await response.json();
    return {
      isValid: data.isValid,
      contactInformation: data.isValid ? data.contactInformation : null,
    };
  } catch (error) {
    console.error('Error validating contact:', error);
    return { isValid: false };
  }
}

// Add a new function for validating subscriptions
async function validateSubscription(id) {
  try {
    // Get Clerk auth token
    const jwt = await getAuthToken();

    const response = await fetch(
      `${searchReportingLegacyUrl}/validate/subscriptions?subscriptionIDs=${[id]}`,
      {
        method: 'GET',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: jwt,
        },
      },
    );

    if (!response.ok) {
      console.error('Network response was not ok');
      return { isValid: false };
    }

    const data = await response.json();
    return {
      isValid: data.isValid,
      subscriptionInformation: data.isValid ? data.subscriptionsInformation[0] : null,
    };
  } catch (error) {
    console.error('Error validating subscription:', error);
    return { isValid: false };
  }
}

// Add a new function for validating forms
async function validateForm(id) {
  try {
    // Get Clerk auth token
    const jwt = await getAuthToken();

    const response = await fetch(`${searchReportingLegacyUrl}/validate/form?formID=${id}`, {
        method: 'GET',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: jwt,
        },
    });

    if (!response.ok) {
      console.error('Network response was not ok');
      return { isValid: false };
    }

    const data = await response.json();
    return {
      isValid: data.isValid,
      formInformation: data.isValid ? data.formInformation[0] : null,
    };
  } catch (error) {
    console.error('Error validating form:', error);
    return { isValid: false };
  }
}

// Add a new function for validating elements
async function validateElement(id) {
  try {
    // Get Clerk auth token
    const jwt = await getAuthToken();

    const response = await fetch(`${searchReportingLegacyUrl}/validate/element?elementID=${id}`, {
        method: 'GET',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: jwt,
        },
    });

    if (!response.ok) {
      console.error('Network response was not ok');
      return { isValid: false };
    }

    const data = await response.json();
    return {
      isValid: data.isValid,
      elementInformation: data.isValid ? data.elementInformation : null,
    };
  } catch (error) {
    console.error('Error validating transaction:', error);
    return { isValid: false };
  }
}

// Add a new function for validating payouts
async function validatePayout(id) {
  try {
    // Get Clerk auth token
    const jwt = await getAuthToken();

    const response = await fetch(`${searchReportingLegacyUrl}/validate/payout?payoutID=${id}`, {
        method: 'GET',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: jwt,
        },
    });

    if (!response.ok) {
      console.error('Network response was not ok');
      return { isValid: false };
    }

    const data = await response.json();
    return {
      isValid: data.isValid,
      payoutInformation: data.isValid ? data.payoutInformation : null,
    };
  } catch (error) {
    console.error('Error validating payout:', error);
    return { isValid: false };
  }
}

// Keep the original DonationsWrapper
function DonationsWrapper(props) {
  const { id } = useParams();
  const [isValidId, setIsValidId] = useState(true);
  const [transactionInfo, setTransactionInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkValidity() {
      if (id) {
        setIsLoading(true);
        try {
          const result = await validateId(id);
          setIsValidId(result.isValid);
          setTransactionInfo(result.transactionInformation);
        } catch (error) {
          console.error('Error validating ID:', error);
          setIsValidId(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    }
    checkValidity();
  }, [id]);

  // Don't wait for validation to render - show page immediately
  // Validation only matters when there's an ID that turns out invalid
  if (id && !isLoading && !isValidId) {
    return <Redirect to="/payments" />;
  }

  return <Donations {...props} transactionInfo={transactionInfo} flag="URL_ARGUMENT" />;
}

// Add a new wrapper for Elements
function ElementsWrapper(props) {
  const { id } = useParams();
  const location = useLocation();
  const noSheet = new URLSearchParams(location.search).get('noSheet');
  const [isValidId, setIsValidId] = useState(true);
  const [elementInfo, setElementInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkValidity() {
      if (id) {
        setIsLoading(true);
        try {
          const result = await validateElement(id);
          setIsValidId(result.isValid);
          setElementInfo(result.elementInformation);
        } catch (error) {
          console.error('Error validating ID:', error);
          setIsValidId(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    }
    checkValidity();
  }, [id]);

  // Don't wait for validation to render - show page immediately
  if (id && !isLoading && !isValidId) {
    return <Redirect to="/elements" />;
  }

  // noSheet=1: show table filtered to element without auto-opening drawer
  if (noSheet && !isLoading) {
    const elementName = elementInfo?.elementName;
    if (!elementName) return <Redirect to="/elements" />;
    return <Elements {...props} initialElementFilter={[elementName]} />;
  }

  return <Elements {...props} elementInfo={elementInfo} flag="URL_ARGUMENT" />;
}

// Add a new wrapper for Contacts
function ContactsWrapper(props) {
  const { id } = useParams();
  const [isValidId, setIsValidId] = useState(true);
  const [contactInfo, setContactInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkValidity() {
      if (id) {
        setIsLoading(true);
        try {
          const result = await validateContact(id);
          setIsValidId(result.isValid);
          setContactInfo(result.contactInformation);
        } catch (error) {
          console.error('Error validating contact ID:', error);
          setIsValidId(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    }
    checkValidity();
  }, [id]);

  // Don't wait for validation to render - show page immediately
  if (id && !isLoading && !isValidId) {
    return <Redirect to="/contacts" />;
  }

  return (
    <MergeProvider>
      <ContactsWithMerge {...props} contactInfo={contactInfo} flag="URL_ARGUMENT" />
    </MergeProvider>
  );
}

// Inner component that can use useMerge hook and pass it to Donors
function ContactsWithMerge(props) {
  const mergeContext = useMerge();

  // Check for in-progress merge on mount
  useEffect(() => {
    if (props.jwtToken && props.merchants?.[0]) {
      mergeContext.checkInProgressMerge(props.jwtToken, props.merchants[0]);
    }
  }, [props.jwtToken, props.merchants]); // eslint-disable-line react-hooks/exhaustive-deps

  return <Donors {...props} mergeContext={mergeContext} />;
}

// Add a new wrapper for Subscriptions
function SubscriptionsWrapper(props) {
  const { id } = useParams();
  const [isValidId, setIsValidId] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkValidity() {
      if (id) {
        setIsLoading(true);
        try {
          const result = await validateSubscription(id);
          setIsValidId(result.isValid);
          setSubscriptionInfo(result.subscriptionInformation);
        } catch (error) {
          console.error('Error validating subscription ID:', error);
          setIsValidId(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    }
    checkValidity();
  }, [id]);

  // Don't wait for validation to render - show page immediately
  if (id && !isLoading && !isValidId) {
    return <Redirect to="/subscriptions" />;
  }

  return <Subscriptions {...props} subscriptionInfo={subscriptionInfo} flag="URL_ARGUMENT" />;
}

function FormsWrapper(props) {
  const { id } = useParams();
  const location = useLocation();
  const noSheet = new URLSearchParams(location.search).get('noSheet');
  const [isValidId, setIsValidId] = useState(true);
  const [formInfo, setFormInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkValidity() {
      if (id) {
        setIsLoading(true);
        setFormInfo(null);
        try {
          const result = await validateForm(id);
          setIsValidId(result.isValid);
          let info = result.formInformation;

          // validateForm returns form-level data only (no tickets/promos).
          // Fetch element data so drilldown tables get the same complete
          // data that FormsTable provides via transformFormWithElements.
          if (info?.elementID) {
            try {
              const resp = await fetch(import.meta.env.VITE_APP_FORMS_URL + '?elements=true', {
                method: 'GET',
                headers: {
                  accept: '*/*',
                  'content-type': 'application/json',
                  Authorization: await getAuthToken(),
                },
              });
              if (resp.ok) {
                const elements = await resp.json();
                const element = elements[info.elementID];
                if (element) {
                  const activeState = info.active;
                  info = { ...info, ...element, active: activeState };
                }
              }
            } catch (e) {
              console.error('Error fetching element data:', e);
            }
          }

          setFormInfo(info);
        } catch (error) {
          console.error('Error validating form ID:', error);
          setIsValidId(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setFormInfo(null);
        setIsLoading(false);
      }
    }
    checkValidity();
  }, [id]);

  // Don't wait for validation to render - show page immediately
  if (id && !isLoading && !isValidId) {
    return <Redirect to="/forms" />;
  }

  // noSheet=1: show table filtered to form without auto-opening drilldown
  if (noSheet && !isLoading) {
    const formName = formInfo?.formName;
    if (!formName) return <Redirect to="/forms" />;
    return <Fundraisers {...props} initialFormFilter={[formName]} setFormInfo={setFormInfo} />;
  }

  return (
    <Fundraisers {...props} formInfo={formInfo} flag="URL_ARGUMENT" setFormInfo={setFormInfo} />
  );
}

function PayoutsWrapper(props) {
  const { id } = useParams();
  const [isValidId, setIsValidId] = useState(true);
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function checkValidity() {
      if (!id) {
        setPayoutInfo(null);
        setIsValidId(true);
        setIsLoading(false);
        return;
      }

      if (payoutInfo?.charityStackID === id) {
        setIsValidId(true);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setPayoutInfo(null);

      try {
        const result = await validatePayout(id);
        if (!isMounted) return;

        setIsValidId(result.isValid);
        setPayoutInfo(result.payoutInformation);
      } catch (error) {
        if (!isMounted) return;

        console.error('Error validating payout ID:', error);
        setIsValidId(false);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }
    checkValidity();

    return () => {
      isMounted = false;
    };
  }, [id, payoutInfo?.charityStackID]);

  // Don't wait for validation to render - show page immediately
  if (id && !isLoading && !isValidId) {
    return <Redirect to="/payouts" />;
  }

  return (
    <Payouts {...props} payoutInfo={payoutInfo} flag="URL_ARGUMENT" setPayoutInfo={setPayoutInfo} />
  );
}

// ============================================================================
// DUAL-AUTH ADAPTER: Clerk User → Cognito-Compatible Format
// ============================================================================
/**
 * Maps Clerk user object to Cognito user format for backward compatibility
 * This allows App component to work with both auth systems without changes
 */
function mapClerkUserToCognitoFormat(clerkUser, activeOrgId) {
  if (!clerkUser) return null;

  // Map Clerk's publicMetadata.readOnly to Cognito's custom:read_only
  const readOnly = clerkUser.publicMetadata?.readOnly === true ? 'true' : 'false';

  // Map only the active Clerk organization to cognito:groups format.
  // Older pages still use getAuthGroups() as a merchant query filter; returning
  // every membership here would turn org switching into cross-org data access.
  const orgMemberships = clerkUser.organizationMemberships || [];
  const activeMembership = activeOrgId
    ? orgMemberships.find((membership) => getMembershipOrganizationId(membership) === activeOrgId)
    : null;
  const activeGroup = activeMembership ? getMembershipDisplayName(activeMembership) : '';
  const groups = activeGroup ? [activeGroup] : [];

  return {
    username: clerkUser.username || clerkUser.id,
    attributes: {
      email: clerkUser.primaryEmailAddress?.emailAddress || '',
      name: clerkUser.fullName || clerkUser.firstName || '',
      'custom:read_only': readOnly,
    },
    signInUserSession: {
      accessToken: {
        payload: {
          'cognito:groups': groups,
        },
      },
    },
  };
}

function ActiveOrganizationSelectionGate() {
  const location = useLocation();
  const history = useHistory();
  const stripeLink = readStripeReviewLink(location);
  const clerk = useClerk();
  const { user: clerkUser } = useUser();
  const {
    isLoaded: organizationListLoaded,
    setActive,
    userMemberships,
  } = useCompleteOrganizationList();
  const [selectingOrgId, setSelectingOrgId] = useState('');
  const [selectionError, setSelectionError] = useState('');

  const membershipList = userMemberships?.data?.length
    ? userMemberships.data
    : clerkUser?.organizationMemberships || [];

  const selectOrganization = async (membership) => {
    const clerkOrgId = getMembershipOrganizationId(membership);
    if (!clerkOrgId || selectingOrgId) return;

    setSelectingOrgId(clerkOrgId);
    setSelectionError('');

    try {
      const { syncResult } = await activateDashboardOrganization({
        clerk,
        setActive,
        clerkOrgId,
        syncContext: 'Syncing active organization after selection',
        redirectUrl:
          window.location.origin +
          ROUTER_BASENAME.replace(/\/$/, '') +
          stripeReviewReturnPath(location),
      });
      if (!syncResult?.ok) {
        console.warn('[ClerkAuth] active-org sync failed after selection', syncResult?.status);
      }
    } catch (err) {
      console.warn('[ClerkAuth] failed to select active organization', err);
      setSelectionError(err?.message || 'Could not open that organization.');
      setSelectingOrgId('');
    }
  };

  return (
    <AuthMarketingSplitLayout leftColumnClassName="flex w-full lg:w-1/2 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md rounded-md border border-edge bg-white shadow-[0_1px_0_rgba(254,254,253,0.95)_inset,0_18px_50px_rgba(58,52,41,0.08)]">
        <div className="border-b border-edge px-5 py-4">
          <img src={CharityStackLogo} alt="CharityStack" className="mb-5 h-5 w-auto" />
          <h1 className="text-lg font-semibold text-ink">Choose an organization</h1>
          <p className="mt-1 text-sm text-ink-muted">
            {stripeLink
              ? 'Select the organization this Stripe link belongs to. Its subscription requirements still apply.'
              : 'Select the workspace you want to open.'}
          </p>
          {stripeLink && (
            <button
              type="button"
              className="mt-3 rounded-md border border-edge bg-paper-muted px-3 py-1.5 text-sm text-ink-secondary"
              onClick={() => history.replace('/')}
            >
              Cancel Stripe review
            </button>
          )}
        </div>

        <div className="px-3 py-3" aria-busy={selectingOrgId ? 'true' : 'false'}>
          {!organizationListLoaded && (
            <div className="space-y-2 px-2 py-1">
              {[0, 1].map((item) => (
                <div key={item} className="h-14 animate-pulse rounded-md bg-paper-inset" />
              ))}
            </div>
          )}

          {organizationListLoaded && membershipList.length === 0 && (
            <div className="rounded-md border border-edge bg-paper-light px-4 py-6 text-center">
              <p className="text-sm font-medium text-ink">No organizations available</p>
              <p className="mt-1 text-sm text-ink-muted">
                Ask an admin to invite you to a workspace.
              </p>
            </div>
          )}

          {organizationListLoaded && membershipList.length > 0 && (
            <div className="space-y-2">
              {membershipList.map((membership) => {
                const clerkOrgId = getMembershipOrganizationId(membership);
                const organization = getMembershipOrganization(membership);
                const orgName = getMembershipDisplayName(membership);
                const isSelecting = selectingOrgId === clerkOrgId;
                const disabled = Boolean(selectingOrgId);

                return (
                  <button
                    key={clerkOrgId || orgName}
                    type="button"
                    disabled={disabled}
                    onClick={() => selectOrganization(membership)}
                    className="group flex w-full items-center justify-between rounded-md border border-edge bg-paper-light px-3 py-3 text-left transition hover:border-sage hover:bg-sage-light disabled:cursor-wait disabled:opacity-70"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      {organization?.imageUrl ? (
                        <img
                          src={organization.imageUrl}
                          alt=""
                          className="h-9 w-9 shrink-0 rounded-md border border-edge bg-white object-cover"
                        />
                      ) : (
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-edge bg-white text-sm font-semibold text-sage">
                          {getOrganizationInitials(orgName)}
                        </span>
                      )}
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ink">
                          {orgName}
                        </span>
                        <span className="block truncate text-xs text-ink-muted">
                          {membership.role?.replace(/^org:/, '') || 'member'}
                        </span>
                      </span>
                    </span>
                    <span className="ml-3 text-sm font-medium text-sage opacity-0 transition group-hover:opacity-100 group-disabled:opacity-100">
                      {isSelecting ? 'Opening...' : 'Open'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {selectionError && (
            <p className="mt-3 rounded-md border border-status-red-text/30 bg-status-red-bg px-3 py-2 text-sm text-status-red-text">
              {selectionError}
            </p>
          )}
        </div>
      </div>
    </AuthMarketingSplitLayout>
  );
}

/**
 * Clerk Authentication Wrapper
 * Uses Clerk hooks and adapts them to Cognito's interface
 */
function ClerkAuthWrapper({ children }) {
  const { isLoaded, isSignedIn, getToken, orgId } = useAuth();
  const { user: clerkUser, isLoaded: userLoaded } = useUser();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { session } = useSession();
  const { signOut: clerkSignOut } = useClerk();
  const [tokenReady, setTokenReady] = useState(false);
  const [isClerkAdmin, setIsClerkAdmin] = useState(false);
  const location = useLocation();
  const lastSyncedActiveOrgIdRef = useRef('');
  // Clerk's auth context is the canonical tenant boundary. The resource hooks can
  // briefly lag it during a switch, while the single-membership fallback covers
  // the first organization created during onboarding.
  const orgMembershipsList = clerkUser?.organizationMemberships ?? [];
  const singleMembershipOrgId =
    orgMembershipsList.length === 1 ? getMembershipOrganizationId(orgMembershipsList[0]) : '';
  const canonicalActiveOrgId = orgId || organization?.id || session?.lastActiveOrganizationId || '';
  const resolvedActiveOrgId = canonicalActiveOrgId || singleMembershipOrgId;
  const devStateLabScope = createDevStateLabScope({
    activeOrgId: resolvedActiveOrgId,
    clerkUser,
  });
  const { state: authWrapperDevState } = useDevStateLabState(devStateLabScope);

  const { skipOrgPermissionsFetch, shouldRedirectToOnboarding, signupComplete } =
    useSignupOnboardingGate(
      location.pathname,
      ROUTER_BASENAME,
      clerkUser,
      isDevStateLabEnabled() ? authWrapperDevState.signup : null,
      resolvedActiveOrgId,
    );

  const onOnboardingPath = isOnboardingRoutePath(location.pathname, ROUTER_BASENAME);

  // A paid marketing CTA stores its selected plan before Clerk resolves whether
  // the visitor is signing in or creating an account. Keep that intent only for
  // a newly-created account that is headed to onboarding; discard it once an
  // existing account reaches the dashboard so it cannot affect a later flow.
  // Wait for useUser() to resolve before trusting the onboarding gate: setActive()
  // can make auth signed-in one render before the new Clerk user is available.
  useEffect(() => {
    if (
      isLoaded &&
      isSignedIn &&
      userLoaded &&
      clerkUser &&
      !onOnboardingPath &&
      !shouldRedirectToOnboarding
    ) {
      clearPlanIntent();
    }
  }, [clerkUser, isLoaded, isSignedIn, onOnboardingPath, shouldRedirectToOnboarding, userLoaded]);

  // Clerk session JWTs omit org claims (`o`) unless getToken({ organizationId }) is used.
  // auth.js calls the stored function with no args — we must inject organizationId here so
  // API Gateway / Lambdas receive authorizer.lambda.orgId (fixes 403 Invalid Access on POST /forms).
  const activeOrgIdRef = useRef('');
  activeOrgIdRef.current = resolvedActiveOrgId;

  const _authDebug = import.meta.env.DEV;

  // Store Clerk's getToken function and groups globally so getAuthToken()/getAuthGroups() work
  useEffect(() => {
    if (isSignedIn && getToken) {
      // Membership list not ready — avoid briefly treating user as having zero orgs.
      if (!userLoaded) {
        if (_authDebug) {
          console.log('[ClerkAuth] skip init: user not loaded yet');
        }
        return;
      }

      const memberCount = orgMembershipsList.length;
      if (memberCount > 0 && !resolvedActiveOrgId) {
        if (_authDebug) {
          console.warn('[ClerkAuth] blocked: have memberships but no active org id yet', {
            memberCount,
            useOrganizationId: organization?.id ?? null,
            sessionLastActiveOrganizationId: session?.lastActiveOrganizationId ?? null,
            singleMembershipOrgId: singleMembershipOrgId || null,
            orgHookLoaded: orgLoaded,
          });
        }
        setTokenReady(false);
        setClerkGetTokenFn(null);
        return;
      }

      let cancelled = false;
      const initAuth = async () => {
        // Extract the active org only. Older pages treat groups as merchant query filters,
        // so multi-org users must not expose every membership here.
        const orgMemberships = clerkUser?.organizationMemberships || [];
        const activeOrgKey = resolvedActiveOrgId || null;
        const activeMembership = activeOrgKey
          ? orgMemberships.find(
              (membership) => getMembershipOrganizationId(membership) === activeOrgKey,
            )
          : undefined;
        const groups = activeMembership ? [getMembershipDisplayName(activeMembership)] : [];
        const formattedGroups = groups.map((group) => group.replace(/-/g, ' '));
        const orgRole = activeMembership?.role || '';
        if (!cancelled) setIsClerkAdmin(orgRole === 'org:admin' || orgRole === 'admin');

        // Extract permission overrides and custom role from org membership public_metadata
        const permissionOverrides = activeMembership?.publicMetadata?.permission_overrides || null;
        const customRoleSlug = activeMembership?.publicMetadata?.custom_role || null;

        // Skip org-role-permissions fetch for gated users (0 orgs, signup not complete) to avoid 403
        const skipOrgPermissions = skipOrgPermissionsFetch;

        const userEmail = clerkUser?.primaryEmailAddress?.emailAddress || '';
        const getTokenWithOrganization = async () => {
          const oid = activeOrgIdRef.current;
          return getToken(oid ? { organizationId: oid } : {});
        };

        const registerClerkAuth = (orgRolePermissions, customRoles) => {
          if (cancelled) return;
          setClerkGetTokenFn(
            getTokenWithOrganization,
            formattedGroups,
            orgRole,
            permissionOverrides,
            orgRolePermissions,
            customRoleSlug,
            customRoles,
            userEmail,
          );
        };

        if (_authDebug) {
          console.log('[ClerkAuth] registering org-scoped getToken', {
            resolvedActiveOrgId: resolvedActiveOrgId || '(none — user-only token)',
            activeOrgSource: orgId
              ? 'useAuth.orgId'
              : organization?.id
                ? 'useOrganization'
                : session?.lastActiveOrganizationId
                  ? 'session.lastActiveOrganizationId'
                  : singleMembershipOrgId
                    ? 'singleMembershipFallback'
                    : 'none',
            memberCount,
          });
        }

        // Register org-scoped getToken before any await so refresh/invalidate cannot use an unscoped getter.
        registerClerkAuth(null, null);
        if (!cancelled) setTokenReady(true);

        let freshActiveOrgToken = null;
        const orgIdForToken = activeOrgIdRef.current;
        if (orgIdForToken) {
          try {
            freshActiveOrgToken = await getToken({
              skipCache: true,
              organizationId: orgIdForToken,
            });
            if (freshActiveOrgToken && !cancelled) {
              primeTokenCache(freshActiveOrgToken);
            }
          } catch (err) {
            console.warn('[ClerkAuth] Failed to prime active-org token:', err);
          }
        }

        if (
          freshActiveOrgToken &&
          orgIdForToken &&
          lastSyncedActiveOrgIdRef.current !== orgIdForToken
        ) {
          lastSyncedActiveOrgIdRef.current = orgIdForToken;
          syncActiveOrganization({
            token: freshActiveOrgToken,
            context: 'Syncing active organization after sign-in',
          })
            .then((result) => {
              if (!result?.ok && result?.status !== 404) {
                console.warn('[ClerkAuth] active-org sync failed', result?.status, result?.error);
              }
            })
            .catch((err) => {
              console.warn('[ClerkAuth] active-org sync error', err);
            });
        }

        if (!skipOrgPermissions) {
          try {
            const token =
              freshActiveOrgToken ||
              (await getToken({
                skipCache: true,
                ...(orgIdForToken ? { organizationId: orgIdForToken } : {}),
              }));
            if (token && !cancelled) {
              const res = await accountsClient.request('GET /org-role-permissions', {
                authorization: token,
                input: {},
              });
              if (res.ok) {
                const data = await res.json();
                registerClerkAuth(data.rolePermissions || null, data.customRoles || null);
                if (_authDebug) {
                  console.log(
                    '[ClerkAuth] org-role-permissions OK — re-registered with role permissions',
                  );
                }
              } else {
                const bodyPreview = (
                  await res
                    .clone()
                    .text()
                    .catch(() => '')
                ).slice(0, 500);
                logAuthDiag('ClerkAuthWrapper: org-role-permissions not ok', {
                  status: res.status,
                  orgIdForToken: orgIdForToken || '(none)',
                  jwtClaims: decodeJwtPayloadSafe(token),
                  responseBodyPreview: bodyPreview || '(empty)',
                });
              }
            }
          } catch (err) {
            console.error('[Auth] Failed to fetch org role permissions:', err);
          }
        }
      };
      initAuth();
      return () => {
        cancelled = true;
      };
    }
  }, [
    isSignedIn,
    getToken,
    orgId,
    clerkUser,
    organization,
    orgLoaded,
    session?.lastActiveOrganizationId,
    resolvedActiveOrgId,
    userLoaded,
    skipOrgPermissionsFetch,
    signupComplete,
  ]);

  if (isAuthRoute(location.pathname)) {
    // Render App directly so the /login, /signup, /request-access routes can mount
    return children({
      signOut: async () => {},
      user: null,
      isAdmin: false,
      clerkUser: null,
      activeOrgId: null,
    });
  }

  // Wait for Clerk to load
  if (!isLoaded) {
    if (onOnboardingPath) {
      return (
        <AuthMarketingSplitLayout leftColumnClassName="flex w-full lg:w-1/2 items-center justify-center">
          <AuthMarketingLoadingState />
        </AuthMarketingSplitLayout>
      );
    }
    return <Loading />;
  }

  // Keep only the allowlisted Stripe destination through Clerk's virtual login.
  const stripeLink = readStripeReviewLink(location);
  if (!isSignedIn) {
    return <Redirect to={{ pathname: '/signin', search: stripeReviewSearch(stripeLink) }} />;
  }
  if (stripeLink && (!resolvedActiveOrgId || shouldRedirectToOnboarding)) {
    return <ActiveOrganizationSelectionGate />;
  }

  if (userLoaded && orgMembershipsList.length > 1 && !resolvedActiveOrgId) {
    return <ActiveOrganizationSelectionGate />;
  }

  // Wait for token to be fetched
  if (!tokenReady) {
    if (onOnboardingPath) {
      return (
        <AuthMarketingSplitLayout leftColumnClassName="flex w-full lg:w-1/2 items-center justify-center">
          <AuthMarketingLoadingState description="Preparing your account..." />
        </AuthMarketingSplitLayout>
      );
    }
    return <Loading />;
  }

  // M2 onboarding: gate dashboard access until signup complete (or user has existing org)
  if (shouldRedirectToOnboarding) {
    return <Redirect to="/onboarding" />;
  }

  if (stripeLink && location.pathname !== STRIPE_REVIEW_PATH) {
    return <Redirect to={stripeReviewReturnPath(location)} />;
  }

  // Map Clerk user to Cognito format
  const cognitoFormattedUser = mapClerkUserToCognitoFormat(clerkUser, resolvedActiveOrgId);

  // Adapt Clerk's signOut to match Cognito's interface
  // NOTE: This signOut is provided to App component but not currently used
  // Logout is handled directly in Sidebar.js using clerk.signOut()
  const signOut = async () => {
    try {
      // Clear our local token function reference
      setClerkGetTokenFn(null);

      // Call Clerk signOut WITHOUT redirectUrl to ensure it completes first
      // This clears the session from Clerk's backend and browser storage
      await clerkSignOut();

      // Redirect after sign-out is complete
      // Use replace to prevent back button from returning to authenticated state
      window.location.replace(window.location.origin + ROUTER_BASENAME);
    } catch (error) {
      console.error('❌ Clerk sign out error:', error);
      // Force redirect even if error
      window.location.replace(window.location.origin + ROUTER_BASENAME);
    }
  };

  // Render App with Cognito-compatible props (clerkUser for SignupFlow/onboarding)
  return children({
    signOut,
    user: cognitoFormattedUser,
    isAdmin: isClerkAdmin,
    clerkUser,
    activeOrgId: resolvedActiveOrgId || null,
  });
}

/**
 * Permission gate for routes. If the user lacks the required permission,
 * keep them in place and explain the missing role capability. The API remains
 * the source of truth for protected data and mutations.
 */
function PermissionGate({ permission, children }) {
  if (!checkPermission(permission)) {
    return <PermissionPlaceholder permission={permission} variant="page" />;
  }
  return children;
}

/**
 * The import wizard route.
 *
 * PE-1584's `imports_tab` PostHog gate was removed at full rollout, and the
 * loading hold + flag latch that used to wrap this route went with it — both
 * existed solely to absorb PostHog's asynchrony. The hold covered the ~2s
 * flag round-trip, during which a bare-boolean gate read silence as "off" and
 * redirected a user who had already dropped their contacts file (F-19); the
 * latch stopped a mid-session flag flip from yanking a live wizard (F-19c).
 * `checkPermission` answers synchronously from the already-loaded Clerk
 * session, so neither failure mode is reachable and neither mechanism has
 * anything left to protect.
 *
 * `IMPORT_CONTACTS` is the remaining, permanent gate — authorization, not
 * rollout — and the backend re-checks it on every call regardless.
 */
function ImportWizardRoute(props) {
  return (
    <PermissionGate permission={P.IMPORT_CONTACTS}>
      <ImportWizardPage {...props} />
    </PermissionGate>
  );
}

/**
 * Entitlement gate for routes — the org-plan axis, distinct from PermissionGate
 * (the user-role axis). They compose, never merge (architecture §3): RBAC fails
 * to a hard hide/redirect; entitlement fails to a visible upsell. So a non-
 * entitled but route-permitted org sees a full-page "upgrade" state rather than
 * a silent redirect.
 *
 * EXCEPTION — `redirectTo`: the "create" (/new) routes opt into a redirect to
 * their parent list instead of the upsell. A create URL is not a meaningful
 * destination for a non-entitled org, and the parent list renders the same upsell
 * in-chrome — so redirecting there (not upselling on a dead-end create page) is the
 * better UX for that specific case. The default (no `redirectTo`) is still upsell.
 *
 * Gate on a single `feature` or `anyOf` a set (e.g. the Elements surface unlocks
 * if the org can use ANY growth element).
 *
 * Degraded-resolution posture is per-surface:
 *   - default (FAIL-OPEN): never wrongly lock a paying org out of a route on a
 *     data error — correct for features inside the legacy-safe bundle (elements,
 *     merge, overlay). The server still enforces.
 *   - `failClosed` (FAIL-CLOSED): degraded → blocked. Use for features OUTSIDE
 *     the legacy-safe bundle (Marketing), where the conservative posture is OFF —
 *     a degraded/unknown org must never gain a feature it was never granted.
 */
// Which MarketingEngagement surface a gated marketing route should show, from
// its path. Segments maps 1:1; everything else (broadcasts) is the default.
// (Email Templates surface removed in PE-1671.)
function marketingSurfaceFromPath(pathname) {
  if (pathname.startsWith('/segments')) return 'segments';
  return 'broadcasts';
}

// The marketing upsell as rendered by a route-level EntitlementGate (for gated
// marketing routes a non-entitled org lands on directly — e.g. /brand-kit, or a
// hard refresh on a gated deep route). The list pages (/broadcasts, /segments)
// render the upsell via MarketingPaywall instead, which fires its OWN
// marketing_upsell_viewed — so these two paths never double-count a single view
// (different routes). Extracted into its own component so the view-event effect
// obeys hooks-order rules (EntitlementGate's render branch sits after early
// returns, where a hook can't live).
function MarketingUpsellGateView({
  surface,
  unlockTier,
  contactHref,
  onUpgrade,
  learnMoreHref,
  orgId,
}) {
  const posthog = usePostHog();
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    if (!posthog) return;
    viewedRef.current = true;
    trackMarketing(posthog, MARKETING_EVENTS.UPSELL_VIEWED, {
      surface,
      unlock_tier: unlockTier,
      source: 'route_gate',
      org_id: orgId,
    });
    // Fire once per mounted surface. eslint-disable to keep single-fire semantics.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface, posthog]);
  return (
    <MarketingEngagement
      surface={surface}
      unlockTier={unlockTier}
      contactHref={contactHref}
      onUpgrade={() => {
        trackMarketing(posthog, MARKETING_EVENTS.UPSELL_CTA_CLICKED, {
          surface,
          cta: 'upgrade',
          unlock_tier: unlockTier,
          source: 'route_gate',
          org_id: orgId,
        });
        onUpgrade();
      }}
      learnMoreHref={learnMoreHref}
    />
  );
}

function EntitlementGate({
  feature,
  anyOf,
  failClosed = false,
  merchant,
  hydrateMerchant,
  redirectTo,
  children,
}) {
  const { can, degraded, ready, isLegacyCustom } = useEntitlements();
  const location = useLocation();
  const history = useHistory();
  // When a route opts into `hydrateMerchant` (the builder routes below), fetch the
  // merchant here so we can show a neutral loading state instead of `children` while
  // entitlements are unresolved — WITHOUT losing recovery (each page normally self-
  // fetches on mount; if we render a spinner instead of the page, that fetch never
  // fires, so the gate does it). No-op when the prop is absent (the hook guards on it),
  // so unrelated routes are unaffected. Must run before any early return (hooks order).
  // When a route opts into `hydrateMerchant`, the gate fetches the merchant itself and
  // reports status, so we can distinguish "still loading" (show the spinner) from "the
  // fetch failed" (fall through to the page so it runs its own error/recovery UI).
  const hydrationStatus = useMerchantHydration(hydrateMerchant);
  // Wiring guard: with neither prop, can(undefined) is false and would silently
  // lock every org out of the route. Fail OPEN (render) and warn in dev so a
  // wiring mistake surfaces in development, never as a production lock-out.
  if (!feature && !anyOf) {
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.error('[EntitlementGate] requires `feature` or `anyOf`; rendering ungated.');
    }
    return children;
  }
  // Merchant not loaded yet (e.g. a hard refresh landing directly on a gated
  // route). Until it loads, entitlements resolve to implicit STARTER, which is
  // indistinguishable from a real Starter org — locking here would wrongly eject
  // an entitled org. Recovery matters too: rendering a lock instead of `children`
  // on a route whose page self-fetches the merchant would prevent that fetch and
  // the lock would NEVER recover. The two postures below (per whether the route
  // opts into gate-owned `hydrateMerchant`) resolve both concerns.
  if (!ready) {
    // Routes WITH `hydrateMerchant` (the Elements routes) must NEVER fall through to
    // `children` while entitlements are unresolved — the builder (`ElementWorkspace`) does
    // not self-fetch the base merchant, so mounting it here would strand a non-entitled
    // org on the builder instead of the upsell (entitlement exposure). So:
    //   • in flight  → centered neutral loading state
    //   • failed     → centered error + retry (reload), still never the builder
    // Both are vertically centered in the content area (the shared loading component is
    // just a centered-text block; without this wrapper it renders top-aligned).
    if (hydrateMerchant) {
      return (
        <div className="flex h-full min-h-[60vh] w-full flex-1 items-center justify-center">
          {hydrationStatus === 'error' ? (
            <div className="text-center space-y-4">
              <p className="text-lg font-medium" style={{ color: '#3A3429' }}>
                We couldn&apos;t load this page
              </p>
              <p className="text-sm" style={{ color: '#6B6458' }}>
                Something went wrong loading your account. Please try again.
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="h-9 rounded-md bg-ink px-4 text-sm font-semibold text-paper-light hover:opacity-90"
              >
                Retry
              </button>
            </div>
          ) : (
            <AuthMarketingLoadingState description="Loading..." />
          )}
        </div>
      );
    }
    // Routes WITHOUT `hydrateMerchant`: original fail-open behavior. The page self-fetches
    // the merchant and the gate re-evaluates on the next render; a brief pre-resolution
    // render to a non-entitled org is acceptable (the server is the authority).
    return children;
  }
  const entitled = degraded ? !failClosed : anyOf ? anyOf.some((f) => can(f)) : can(feature);
  const entitlementOrgId =
    merchant?.organizationId || merchant?.organizationID || merchant?.merchantID || merchant?.id;
  if (!entitled) {
    // Opt-in redirect: a route may bounce a non-entitled org up to its parent list
    // instead of showing a full-page upsell. Used by the "create" (/new) routes —
    // a "create" URL is not a meaningful destination for a non-entitled org, and the
    // parent list already renders its own in-page upsell (MarketingPaywall /
    // ElementsEngagement). This sits AFTER the `!ready` early-return above, so an
    // entitled org mid-hydration is never wrongly bounced (it gets the loading state,
    // then renders `children` once `ready`); a hydrateMerchant fetch error likewise
    // stays on the retry card rather than redirecting.
    //
    // NOT for degraded orgs: a degraded record means the engine couldn't resolve the
    // billing state (a data-integrity bug), and on a failClosed route that computes as
    // not-entitled. We deliberately do NOT redirect those — a silent bounce to the
    // list would look like normal navigation and mask the corruption. Instead they
    // fall through to the upsell (the pre-redirect behavior for degraded orgs), which
    // at least signals the gate. Redirect only a cleanly non-entitled org.
    if (redirectTo && !degraded) {
      return <Redirect to={redirectTo} />;
    }
    // Marketing routes show the rich engagement/upsell (PE-1651) — the SAME
    // surface the marketing list pages render via MarketingPaywall — instead of
    // the generic lock, so the whole marketing area is consistent. Detected by
    // the marketing feature-key set; the surface is inferred from the path.
    if (anyOf === MARKETING_FEATURE_KEYS) {
      // PE-1678 — a legacy_custom (managed-account) org sees "Contact us" instead
      // of the self-serve upgrade CTA (its plan is changed by our team, not via
      // checkout). Personalize the draft with the org name from the merchant prop
      // (threaded from the route) so a direct landing on a gated marketing route
      // gets the SAME merchant-specific draft as MarketingPaywall. Falls back to
      // the name-less draft if merchant hasn't hydrated yet.
      const contactHref = isLegacyCustom
        ? buildContactUsMailto({ orgName: merchant?.merchantName })
        : undefined;
      return (
        <MarketingUpsellGateView
          surface={marketingSurfaceFromPath(location.pathname)}
          unlockTier={unlockTierLabel(FEATURE.BROADCASTS)}
          contactHref={contactHref}
          onUpgrade={() => history.push('/settings/billing')}
          learnMoreHref={growPageUrl()}
          orgId={entitlementOrgId}
        />
      );
    }
    // Elements routes show the rich engagement/upsell (PE-1624) — fundraising
    // boosters positioned as what Base unlocks — instead of the generic lock, so a
    // Starter org that clicks the (always-visible) Elements nav item lands on a
    // value-led page, not a dead-end. Detected by the elements feature-key set.
    if (anyOf === ELEMENT_FEATURE_KEYS) {
      return (
        <ElementsEngagement
          unlockTier={unlockTierLabel(FEATURE.FUNDRAISING_BAR)}
          onUpgrade={() => history.push('/settings/billing')}
          onLearnMore={() => history.push('/settings/billing')}
        />
      );
    }
    // Other gated surfaces: the minimal generic lock. anyOf entries all unlock at
    // the same tier today, so anyOf[0] yields the correct "Available on {tier}" copy.
    return <UpgradeLock feature={anyOf ? anyOf[0] : feature} variant="page" />;
  }
  return children;
}

// Model the ORGANIZATION as a PostHog group (PE-1743). Today orgId rides along as
// an event property only, so funnels can't be read per-account — the unit a B2B
// PM actually reasons about. Registering the org as a group makes activation,
// conversion, and revenue-lift cohorts answerable at the org level, and lets
// billing/marketing events (which pass org_id) attach to a first-class group.
//
// Group properties are the DURABLE org facts: the stored plan slug and its tier.
// We read billingDetails.plan raw (defaulting to STARTER) rather than through the
// entitlement engine to keep this wiring dependency-free and side-effect-light —
// the plan slug is the stable value; a coarse tier is derived from it for
// breakdowns. Non-blocking, guarded, and fired only once the merchant (with an
// org id) has hydrated.
//
// FLAG-1 (DK-72 a): the org slug rides along as group property `slug`, the
// property the `multi-currency` flag's release condition matches on. posthog-js
// also feeds group properties into flag evaluation and reloads flags, so a slug
// condition matches on this load. Omitted (no key) when the merchant has none.
export function useOrganizationPostHogGroup(posthog, merchant) {
  useEffect(() => {
    const orgId = merchant?.organizationId || null;
    if (!orgId) {
      if (merchant) {
        console.warn(
          '[Dashboard] PostHog organization group skipped; organizationId missing from merchant payload',
        );
      }
      return;
    }
    if (!posthog?.group) return;
    try {
      const plan = merchant?.billingDetails?.plan || 'STARTER';
      const tier = String(plan).toLowerCase();
      posthog.group('organization', String(orgId), {
        plan,
        tier,
        // Absent means uncovered, matching both entitlement engines
        // (resolver.ts:508, org_utils.py:689). `!== false` tagged an org with
        // no persisted value as covered, which would skew any PostHog cohort
        // split on fee mode.
        covered_commitment: merchant?.billingDetails?.coveredCommitment === true,
        merchant_name: merchant?.merchantName,
        ...(merchant?.organizationSlug ? { slug: merchant.organizationSlug } : {}),
      });
    } catch (error) {
      console.error('[Dashboard] PostHog group failed (non-blocking):', error);
    }
  }, [
    merchant?.organizationId,
    merchant?.organizationSlug,
    merchant?.billingDetails?.plan,
    merchant?.billingDetails?.coveredCommitment,
    merchant?.merchantName,
    posthog,
  ]);
}

function App({ signOut, user, isAdmin = false, clerkUser = null, activeOrgId = null }) {
  const { isDocked: isAssistantDocked, isExpanded: isAssistantExpanded } =
    useDashboardAssistantDock();
  const shouldReduceAssistantMotion = useReducedMotion();
  const posthog = usePostHog();
  // PE-1584's import-tool flag is NOT read here. The wizard route needs a
  // latch scoped to the route visit, and `App` never unmounts — see
  // ImportWizardRoute.

  // ── Maintenance Mode (PostHog feature flag) ──────────────────────────
  // Try cached flags synchronously on mount — avoids a loading spinner on every page load.
  // Lazy initializers run exactly once (React guarantee), so two isFeatureEnabled calls
  // on mount is the correct tradeoff vs. an IIFE that would run on every render.
  const [maintenanceMode, setMaintenanceMode] = useState(() => {
    try {
      return !!posthog?.isFeatureEnabled?.('dashboard_maintenance_mode');
    } catch {
      return false;
    }
  });
  const [maintenanceChecked, setMaintenanceChecked] = useState(() => {
    // If cached OFF → skip spinner (fast happy path).
    // If cached ON → wait for server confirmation before showing maintenance page,
    // preventing a jarring flash if the flag was turned off since the last visit.
    try {
      const cached = posthog?.isFeatureEnabled?.('dashboard_maintenance_mode');
      return cached === false;
    } catch {
      return false;
    }
  });

  // Tracks whether the user has been in the dashboard long enough for a real session.
  // Prevents stale cached flags from triggering the mid-session warning toast on first load.
  // The 5s session timer only starts after maintenanceChecked is true (i.e., after the
  // first onFeatureFlags fires OR the 3s fail-open timeout), eliminating the 3s–5s
  // danger window where a late PostHog response could silently redirect without a toast.
  const hasRenderedBeyondInitialLoad = useRef(false);
  const hasServerConfirmedMaintenanceMode = useRef(false);

  useEffect(() => {
    if (!posthog) {
      setMaintenanceChecked(true); // fail-open: no PostHog → show dashboard
      return;
    }

    // Sentinel: -1 means maintenance was applied on first load (not mid-session).
    // Prevents the mid-session toast from firing on subsequent 30s poll cycles
    // when the user is already on the maintenance page.
    let maintenanceRedirectTimeout = null;

    // Timeout fallback — don't block the app if PostHog is slow on first load.
    // Declared before onFeatureFlags so the callback can cancel it.
    let timeout = setTimeout(() => {
      setMaintenanceMode(false); // don't trust stale cache if PostHog is unreachable
      setMaintenanceChecked(true); // fail-open after 3s
    }, 3000);

    // Keep listener active — fires on initial load AND whenever flags are updated
    // from PostHog (e.g., flag toggled while user is in the dashboard).
    const unsubscribe = posthog.onFeatureFlags((_flags, _variants, featureFlagContext = {}) => {
      clearTimeout(timeout); // PostHog responded — cancel the fail-open fallback
      const flagOn = !!posthog.isFeatureEnabled('dashboard_maintenance_mode');
      const flagsLoadedFromRemote =
        featureFlagContext.errorsLoading === false ||
        posthog.featureFlags?._flagsLoadedFromRemote === true;
      if (flagOn && flagsLoadedFromRemote) {
        hasServerConfirmedMaintenanceMode.current = true;
      }
      setMaintenanceChecked(true);

      if (flagOn && hasRenderedBeyondInitialLoad.current && !maintenanceRedirectTimeout) {
        // Flag toggled ON mid-session — warn user before redirecting
        toast.warning('Maintenance starting shortly — please save your work.', {
          duration: 10000,
        });
        maintenanceRedirectTimeout = setTimeout(() => setMaintenanceMode(true), 10000);
      } else if (!flagOn) {
        // Flag turned off — cancel any pending redirect
        if (maintenanceRedirectTimeout && maintenanceRedirectTimeout !== -1) {
          clearTimeout(maintenanceRedirectTimeout);
        }
        maintenanceRedirectTimeout = null;
        setMaintenanceMode(false);
      } else if (!hasRenderedBeyondInitialLoad.current) {
        // First load (or stale cached state) — apply immediately, no toast
        maintenanceRedirectTimeout = -1; // sentinel: already on maintenance page
        setMaintenanceMode(flagOn);
      }
    });

    // Mark as "in-session" after 5s from mount. Any flag response within
    // this window (including late PostHog responses after the 3s fail-open)
    // is treated as initial load → immediate redirect, no toast. This is
    // correct because a user who arrived ≤5s ago has no unsaved work to lose.
    // Only flag changes AFTER 5s trigger the warning toast.
    const sessionTimer = setTimeout(() => {
      hasRenderedBeyondInitialLoad.current = true;
    }, 5000);

    // Keep a light-touch refresh for mid-session maintenance toggles without
    // the 30s request volume that was burning through feature_flag_requests.
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') {
        posthog.reloadFeatureFlags();
      }
    }, 300000); // check every 5 minutes while the tab is visible

    return () => {
      clearTimeout(timeout);
      clearTimeout(sessionTimer);
      if (maintenanceRedirectTimeout && maintenanceRedirectTimeout !== -1) {
        clearTimeout(maintenanceRedirectTimeout);
      }
      clearInterval(poll);
      unsubscribe?.();
    };
  }, [posthog]);

  // When maintenance ends, force reload to pick up any new frontend deploy
  // that likely accompanied the backend changes.
  const prevMaintenanceMode = useRef(maintenanceMode);
  useEffect(() => {
    if (
      hasServerConfirmedMaintenanceMode.current &&
      prevMaintenanceMode.current === true &&
      maintenanceMode === false
    ) {
      window.location.reload();
    }
    prevMaintenanceMode.current = maintenanceMode;
  }, [maintenanceMode]);
  // ── End Maintenance Mode ─────────────────────────────────────────────

  // Stripe Connect account_sessions/claim errors on iOS are suppressed via
  // ignoreErrors in @charitystack/shared sentry-config.js (not here).
  // Sentry's listener registers before React mounts, so event.preventDefault()
  // in a useEffect would run too late. The ignoreErrors pattern drops the event
  // before Sentry ever captures it. See: CHARITYSTACK-MONOREPO-AV.

  // True while the Marketing onboarding tour modal is open — suppresses the
  // Productlane bubble (the tour dispatches MARKETING_TOUR_VISIBILITY_EVENT).
  const location = useLocation();
  const dashboardWorkspaceViewRef = useRef(null);
  const [selectableFunds, setSelectableFunds] = useState([]);
  const [selectableForms, setSelectableForms] = useState([]);
  // const [readOnly, setReadOnly] = useState(null);

  const [jwtToken, setJWTToken] = useState(null);
  const [merchant, setMerchant] = useScopedMerchant(activeOrgId || clerkUser?.id || null);
  const [merchantRefreshKey, setMerchantRefreshKey] = useState(0);
  const renderDashboardRoutesRef = useRef(null);
  const [orgRouteCacheScope, setOrgRouteCacheScope] = useState(activeOrgId || 'no-active-org');
  const orgRouteCacheScopeRef = useRef(orgRouteCacheScope);
  const renderDashboardRoutes = useCallback(
    (switchLocation) => renderDashboardRoutesRef.current?.(switchLocation) ?? null,
    [],
  );
  const resetOrgBoundDashboardState = useCallback(() => {
    setMerchant(null);
    setSelectableFunds([]);
    setSelectableForms([]);
    setJWTToken(null);
    renderDashboardRoutesRef.current = null;
    writeDashboardTabsState({
      tabs: [createDashboardFallbackTab('/')],
      activeTabId: 'fallback:/',
    });
  }, [setMerchant]);

  useEffect(() => {
    orgRouteCacheScopeRef.current = orgRouteCacheScope;
  }, [orgRouteCacheScope]);

  useEffect(() => {
    const nextOrgRouteCacheScope = activeOrgId || 'no-active-org';
    if (orgRouteCacheScopeRef.current === nextOrgRouteCacheScope) {
      return;
    }
    orgRouteCacheScopeRef.current = nextOrgRouteCacheScope;
    setOrgRouteCacheScope(nextOrgRouteCacheScope);
    resetOrgBoundDashboardState();
  }, [activeOrgId, resetOrgBoundDashboardState]);

  // Seed merchant from router location state if present (set by SignupFlow after org creation)
  // so the shell has org name/step before grabBaseMerchant(); pre-form invite waits until GET hydrates
  // (see isRouterMerchantSeedPendingHydration in DashboardOnboardingChrome).
  // useLayoutEffect (not useEffect) ensures the state update triggers a synchronous re-render
  // BEFORE the browser paints — the user never sees the naked dashboard.
  useLayoutEffect(() => {
    const seed = location.state?.merchantSeed;
    if (seed) {
      setMerchant((prev) => prev ?? seed);
      // Clear the seed from history so back/forward doesn't re-apply stale data.
      window.history.replaceState({ ...window.history.state, merchantSeed: undefined }, '');
    }
  }, [location.state?.merchantSeed, setMerchant]);
  // const [user, setUser] = useState(undefined); // State to hold user information
  const [groups, setGroups] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const devStateLabScope = createDevStateLabScope({ activeOrgId, merchant, clerkUser });
  const devStateLab = useDevStateLabState(devStateLabScope);
  const effectiveMerchant = useMemo(
    () => (isDevStateLabEnabled() ? applyDevStateOverrides(merchant, devStateLab.state) : merchant),
    [merchant, devStateLab.state],
  );
  const effectiveIsAdmin =
    isDevStateLabEnabled() && devStateLab.state.auth?.effectiveRole
      ? devStateLab.state.auth.effectiveRole === ROLES.ADMIN
      : isAdmin;

  useEffect(() => {
    const organizationId = effectiveMerchant?.organizationId;
    if (!posthog || isAuthRoute(location.pathname) || !organizationId) return;

    const workspace = dashboardWorkspaceFromPath(location.pathname);
    const viewKey = dashboardWorkspaceViewKey(String(organizationId), location.pathname);
    if (dashboardWorkspaceViewRef.current === viewKey) return;
    dashboardWorkspaceViewRef.current = viewKey;

    try {
      posthog.group?.('organization', String(organizationId));
    } catch (error) {
      console.error('[Dashboard] PostHog group failed (non-blocking):', error);
    }

    trackDashboardPlg(posthog, DASHBOARD_PLG_EVENTS.WORKSPACE_VIEWED, {
      workspace,
      onboarding_state: effectiveMerchant?.onboardingCurrentStep || 'not_started',
      plan: effectiveMerchant?.billingPlan,
    });
  }, [
    effectiveMerchant?.billingPlan,
    effectiveMerchant?.onboardingCurrentStep,
    effectiveMerchant?.organizationId,
    location.pathname,
    posthog,
  ]);

  const { isActivationMode } = useDashboardOnboardingUi({
    merchant: effectiveMerchant,
    pathname: location.pathname,
    isAuthRoute,
  });

  // Wrapper for setMerchant that prevents unnecessary re-renders from identical data
  // Problem: Every page calls grabBaseMerchant() which fetches merchant data and calls setMerchant()
  // Even though the data is identical, the new object reference triggers React re-renders
  // This caused the Sidebar logo to refresh on every navigation (useEffect dependency on merchant)
  // Solution: Compare key fields and only update if something actually changed
  const updateMerchant = useCallback((newMerchantDataOrUpdater) => {
    // Functional form intentionally bypasses hasChanged so callers can do partial updates (e.g. payout settings).
    if (typeof newMerchantDataOrUpdater === 'function') {
      setMerchant(newMerchantDataOrUpdater);
      return;
    }
    const newMerchantData = newMerchantDataOrUpdater;

    setMerchant((prevMerchant) => {
      // If no previous merchant, always update
      if (!prevMerchant) {
        return newMerchantData;
      }
      // Check if key fields have changed (including timezone for settings updates)
      // Normalize null/undefined to null for consistent comparison
      const hasChanged =
        prevMerchant.merchantName !== newMerchantData?.merchantName ||
        prevMerchant.id !== newMerchantData?.id ||
        prevMerchant.organizationId !== newMerchantData?.organizationId ||
        prevMerchant.logoUrl !== newMerchantData?.logoUrl ||
        prevMerchant.organizationLogoUrl !== newMerchantData?.organizationLogoUrl ||
        prevMerchant.logoImageUrl !== newMerchantData?.logoImageUrl ||
        prevMerchant.updatedAt !== newMerchantData?.updatedAt ||
        prevMerchant.lastUpdated !== newMerchantData?.lastUpdated ||
        prevMerchant.defaultPaymentProcessor !== newMerchantData?.defaultPaymentProcessor ||
        JSON.stringify(prevMerchant.stripeDetails) !==
          JSON.stringify(newMerchantData?.stripeDetails) ||
        prevMerchant.stripeAccountID !== newMerchantData?.stripeAccountID ||
        (prevMerchant.timezone || null) !== (newMerchantData?.timezone || null) ||
        prevMerchant.address1 !== newMerchantData?.address1 ||
        prevMerchant.address2 !== newMerchantData?.address2 ||
        prevMerchant.city !== newMerchantData?.city ||
        prevMerchant.state !== newMerchantData?.state ||
        prevMerchant.zip !== newMerchantData?.zip ||
        prevMerchant.EIN !== newMerchantData?.EIN ||
        prevMerchant.payoutFrequency !== newMerchantData?.payoutFrequency ||
        prevMerchant.payoutArrivalDay !== newMerchantData?.payoutArrivalDay ||
        prevMerchant.brandColor !== newMerchantData?.brandColor ||
        prevMerchant.portalPrimaryColor !== newMerchantData?.portalPrimaryColor ||
        prevMerchant.onboardingCurrentStep !== newMerchantData?.onboardingCurrentStep ||
        prevMerchant.hasWebsite !== newMerchantData?.hasWebsite ||
        prevMerchant.lastPublishedFormId !== newMerchantData?.lastPublishedFormId ||
        prevMerchant.billingPlan !== newMerchantData?.billingPlan ||
        prevMerchant.checkoutBumpCSSharePercent !== newMerchantData?.checkoutBumpCSSharePercent ||
        prevMerchant.dashboardPercentFee !== newMerchantData?.dashboardPercentFee ||
        prevMerchant.dashboardFixedFee !== newMerchantData?.dashboardFixedFee ||
        JSON.stringify(prevMerchant.billingDetails || {}) !==
          JSON.stringify(newMerchantData?.billingDetails || {}) ||
        JSON.stringify(prevMerchant.feeAmounts || {}) !==
          JSON.stringify(newMerchantData?.feeAmounts || {}) ||
        JSON.stringify(prevMerchant.driverFlowsCompleted || {}) !==
          JSON.stringify(newMerchantData?.driverFlowsCompleted || {}) ||
        JSON.stringify(prevMerchant.organizationBaseURLs || []) !==
          JSON.stringify(newMerchantData?.organizationBaseURLs || []) ||
        // Settings → Organization fields (PE-2047/PE-2048): without these,
        // a save that changes only e.g. privacyPolicyLink keeps the OLD
        // merchant reference, so Organization.jsx's [merchant] rehydration
        // effect never reruns and the form stays out of sync with the server
        // (hasChanges stays true after a successful save).
        prevMerchant.orgDescription !== newMerchantData?.orgDescription ||
        prevMerchant.websiteUrl !== newMerchantData?.websiteUrl ||
        prevMerchant.privacyPolicyLink !== newMerchantData?.privacyPolicyLink ||
        prevMerchant.organizationSlug !== newMerchantData?.organizationSlug ||
        Boolean(prevMerchant.privacyOverlayEnabled) !==
          Boolean(newMerchantData?.privacyOverlayEnabled) ||
        prevMerchant.marketingPhone !== newMerchantData?.marketingPhone ||
        JSON.stringify(prevMerchant.registrationContact || {}) !==
          JSON.stringify(newMerchantData?.registrationContact || {});

      if (hasChanged) {
        return newMerchantData;
      }
      // Nothing changed, keep same object reference to prevent re-renders
      return prevMerchant;
    });
  }, [setMerchant]);

  // Shell chrome must not depend on whichever page happened to mount first.
  // Hydrate once for the active Clerk organization so cold deep links have the
  // same merchant identity, logo, and entitlement context as list navigation.
  const merchantHydrationEnabled = Boolean(activeOrgId) && !isAuthRoute(location.pathname);
  const merchantHydrationStatus = useMerchantHydration(activeOrgId ? updateMerchant : undefined, {
    enabled: merchantHydrationEnabled,
    scopeKey: activeOrgId,
    context: 'dashboard shell merchant hydration',
    refreshKey: merchantRefreshKey,
  });

  const { readOnly, readOnlySet } = useSelector((state) => state.readOnly);
  const dispatch = useDispatch();
  const [, forceDevAuthRender] = useState(0);

  useLayoutEffect(() => {
    if (!isDevStateLabEnabled()) return undefined;
    setDevAuthOverride(devStateLab.state.auth);
    forceDevAuthRender((version) => version + 1);
    return () => {
      clearDevAuthOverride();
    };
  }, [devStateLab.state.auth]);

  // Update the auth effect to use the user prop
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const groups = user?.signInUserSession?.accessToken?.payload?.['cognito:groups'] ?? [];
        const formattedGroups = groups.map((group) => group.replace(/-/g, ' '));
        setGroups(formattedGroups);
      } catch (error) {
        console.error('Error checking auth status:', error);
        setGroups(null);
      }
    };

    checkAuth();
  }, [user]);

  useEffect(() => {
    if (!posthog) return;
    if (!user) {
      posthog.reset?.();
      return;
    }
    if (!clerkUser?.id) return;

    try {
      posthog.identify?.(clerkUser.id);
    } catch (error) {
      console.error('[Dashboard] PostHog identify failed (non-blocking):', error);
    }
  }, [clerkUser?.id, posthog, user]);

  useOrganizationPostHogGroup(posthog, merchant);

  // update read_only state based on user
  useEffect(() => {
    if (user === undefined || readOnlySet === true) {
      // We can't determine the current authentication state yet or it has already been set
    } else if (user === null) {
      // User is not logged in
      dispatch(setReadOnly(false));
      // Clear Sentry user context
      Sentry.setUser(null);
    } else {
      // Set Sentry user context
      Sentry.setUser({
        id: user.username,
        email: user.attributes.email,
        username: user.attributes.name || user.username,
        segment: groups ? groups.join(',') : 'unknown',
      });

      // Set custom tags for better filtering
      const effectiveRole = getEffectiveRole(); // e.g. "org:editor" or "custom:volunteer_coordinator"
      const customRole = getCustomRole(); // e.g. "volunteer_coordinator" or null

      Sentry.setTag(
        'user_role',
        effectiveRole || (user.attributes['custom:read_only'] === 'true' ? 'readonly' : 'admin'),
      );
      if (customRole) {
        Sentry.setTag('custom_role', customRole);
      }
      Sentry.setTag('user_groups', groups ? groups.join(',') : 'none');
      Sentry.setTag('finix_environment', import.meta.env.VITE_APP_FINIX_ENVIRONMENT);
      Sentry.setTag('environment', import.meta.env.VITE_APP_ENVIRONMENT);

      // User is logged in
      dispatch(setReadOnly(user.attributes['custom:read_only'] === 'true'));
    }
  }, [user, location.pathname]);

  useEffect(() => {
    if (!isDevStateLabEnabled()) return;
    if (typeof devStateLab.state.readOnly === 'boolean') {
      dispatch(setReadOnly(devStateLab.state.readOnly));
    } else if (user?.attributes) {
      dispatch(setReadOnly(user.attributes['custom:read_only'] === 'true'));
    }
  }, [devStateLab.state.readOnly, dispatch, user]);

  // Productlane widget: enable only for authenticated users on real dashboard pages.
  // Separate effect so it always re-evaluates on pathname changes (the readOnlySet
  // guard above short-circuits after the first render, which would skip this logic).
  // CSS suppresses Productlane's default launcher; our bottom bar owns the trigger.
  // Productlane is a FIRST-RUN nudge, not a permanent fixture: it auto-appears
  // only during a new user's first few sessions (PRODUCTLANE_MAX_SESSIONS), then
  // never again — the bottom-bar "Help Center" button still opens it on demand.
  // Record the session once per browser session (sessionStorage-guarded inside
  // recordProductlaneSessionOnce) the first time we have a user id, and keep the
  // count in state so the gating effect re-evaluates against a stable value.
  const [productlaneSessionCount, setProductlaneSessionCount] = useState(0);
  useEffect(() => {
    if (!user?.username) return;
    setProductlaneSessionCount(recordProductlaneSessionOnce(user.username));
  }, [user?.username]);

  useEffect(() => {
    if (!window.Productlane) return;
    const onAuthOrOnboarding =
      isAuthRoute(location.pathname) ||
      pathnameTouchesOnboardingForProductlane(location.pathname, ROUTER_BASENAME);
    // Keep the widget available to our custom Help Center trigger on normal
    // authenticated pages. Only its automatic changelog notification is gated
    // to the first few sessions.
    const withinSessionWindow = isWithinProductlaneSessionWindow(productlaneSessionCount);
    const isAvailable = Boolean(user) && !onAuthOrOnboarding;

    configureProductlaneWidget(window.Productlane, {
      isAvailable,
      userEmail: user?.attributes?.email,
      showChangelogNotification: withinSessionWindow,
    });
  }, [user, location.pathname, productlaneSessionCount]);

  const stripeEntry = readStripeReviewLink(location);
  const stripeSession = useStripeSessionLifecycle({
    merchant,
    userId: clerkUser?.id || user?.username,
    activeOrgId,
    canManage:
      checkPermission(P.MANAGE_PROCESSOR_SETTINGS) &&
      (!stripeEntry || stripeReviewMatches(stripeEntry, merchant, activeOrgId)),
    permissionScope: [...getUserPermissions()].sort().join(':'),
    unsupportedBrowser: isIOSWebView,
  });
  const stripeConnectInstance = stripeSession.instance;
  const stripeConnectError = stripeSession.error;

  // Track merchant context for Sentry
  useEffect(() => {
    if (merchant) {
      Sentry.setTag('merchant_name', merchant.name || 'unknown');
      Sentry.setTag('merchant_type', merchant.type || 'unknown');
      Sentry.setContext('merchant', {
        organizationId: merchant.organizationId,
        id: merchant.id,
        recordId: getMerchantRecordId(merchant),
        name: merchant.name,
        type: merchant.type,
      });
    }
  }, [merchant]);

  useEffect(() => {
    document.querySelector('html').style.scrollBehavior = 'auto';
    window.scroll({ top: 0 });
    document.querySelector('html').style.scrollBehavior = '';
    focusHandling('outline');
  }, [location.pathname]); // triggered on route change

  const getFormStatus = async (formID) => {
    // Get Clerk auth token
    const jwtToken = await getAuthToken();
    const url = `${
      import.meta.env.VITE_APP_FORMS_URL
    }/get-form-name-update-status?formID=${formID}`;
    try {
      const result = await safeFetchJSON(
        url,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: jwtToken,
          },
        },
        {
          context: 'Fetching form name update status',
          retries: 2,
          retryDelay: 1000,
        },
      );

      if (!result.ok) {
        console.error('Error fetching form status:', result.error);
        return null;
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching form status:', error);
      return null; // Return null or handle the error as needed
    }
  };

  useEffect(() => {
    const status = [
      'donationChangingNameStatus',
      'elementChangingNameStatus',
      'payoutChangingNameStatus',
      'subscriptionChangingNameStatus',
    ];
    const interval = setInterval(async () => {
      const formIDs = JSON.parse(localStorage.getItem('formIDs') || '[]');
      if (formIDs.length > 0) {
        for (const formID of formIDs) {
          const data = await getFormStatus(formID);
          if (status.every((key) => data?.formNameStatus?.[key] === 'COMPLETED')) {
            // Add breadcrumb for successful form update
            Sentry.addBreadcrumb({
              message: 'Form name change completed successfully',
              category: 'form',
              level: 'info',
              data: { formId: formID },
            });

            toast.success(
              'Form name change completed successfully! 🎉 Please Refresh the page to see the changes.',
              {
                duration: 5000,
              },
            );
            const updatedFormIDs = formIDs.filter((id) => id !== formID);
            localStorage.setItem('formIDs', JSON.stringify(updatedFormIDs));
          } else if (status.some((key) => data?.formNameStatus?.[key] === 'FAILED')) {
            // Add breadcrumb for failed form update
            Sentry.addBreadcrumb({
              message: 'Form name change failed',
              category: 'form',
              level: 'error',
              data: { formId: formID, status: data?.formNameStatus },
            });

            toast.error('Form name change failed. Please contact support.', {
              duration: 5000,
            });
          }
        }
      }
    }, 30000); // every 30 seconds

    return () => clearInterval(interval); // clean up on unmount
  }, []);

  // Render auth pages immediately without waiting for groups to load.
  // This prevents a flash of <Loading /> when Clerk navigates between
  // sign-in sub-paths (e.g., /signin → /signin/factor-two for SMS 2FA).
  // ── Maintenance Mode Gate ─────────────────────────────────────────────
  // Checked after all hooks, before any route rendering.
  // NOTE: useEffect hooks above (auth, Stripe, etc.) still fire during maintenance
  // because React requires hooks to be called unconditionally. This may produce
  // noisy console errors when the backend is down — this is expected and harmless.
  if (maintenanceChecked && maintenanceMode) {
    return <MaintenancePage />;
  }
  if (!maintenanceChecked) {
    if (user && isOnboardingRoutePath(location.pathname, ROUTER_BASENAME)) {
      return (
        <AuthMarketingSplitLayout leftColumnClassName="flex w-full lg:w-1/2 items-center justify-center">
          <AuthMarketingLoadingState description="Loading..." />
        </AuthMarketingSplitLayout>
      );
    }
    // Public auth funnel: do not block on PostHog — avoids dashboard <Loading /> while
    // flags resolve (and on full reload when navigating sign-in ↔ sign-up).
    if (!isAuthRoute(location.pathname)) {
      return <Loading />;
    }
  }
  // ── End Maintenance Mode Gate ────────────────────────────────────────

  if (isAuthRoute(location.pathname)) {
    return (
      <AuthMarketingSplitLayout leftColumnClassName="relative flex flex-1 min-w-0 flex-col h-screen min-h-0 overflow-y-auto overflow-x-hidden">
        {/* flex-1 so sign-in/sign-up content fills the left column and vertical centering works */}
        <div className="flex min-h-0 flex-1 flex-col">
          <Switch>
            <Route path="/signin">
              <SignIn />
            </Route>
            <Route path="/signup">
              <SignUp />
            </Route>
            <Route path="/request-access">
              <RequestAccess />
            </Route>
          </Switch>
        </div>
      </AuthMarketingSplitLayout>
    );
  }

  if (user && groups === null && isOnboardingRoutePath(location.pathname, ROUTER_BASENAME)) {
    return (
      <AuthMarketingSplitLayout leftColumnClassName="flex w-full lg:w-1/2 items-center justify-center">
        <AuthMarketingLoadingState description="Preparing your workspace..." />
      </AuthMarketingSplitLayout>
    );
  }

  const rawMerchantForDevStateLab = merchant;

  if (
    stripeReviewNeedsOrganizationSelection({
      subscriptionRequired: isActivationMode,
      link: stripeEntry,
      merchant,
      activeOrgId,
    })
  ) {
    return <ActiveOrganizationSelectionGate />;
  }

  if (groups !== null) {
    // UI rendering sees Dev State Lab simulations; backend-facing effects above use raw merchant.
    const merchant = effectiveMerchant;

    // Single source of truth for FormWorkspace props — consumed by both the main
    // routes (lines ~1893, ~1912 below) and the activation-allowlist copies in
    // this Switch. Spreading prevents prop drift: any prop added here flows to
    // every <FormWorkspace> instance automatically.
    const formWorkspaceProps = {
      jwtToken,
      setJWTToken,
      selectableFunds,
      setSelectableFunds,
      merchant,
      setMerchant: updateMerchant,
      selectableForms,
      setSelectableForms,
      groups,
      user,
      sidebarOpen,
      setSidebarOpen,
    };
    const hasExistingOrgForSignupRoute =
      typeof devStateLab.state.signup?.hasExistingOrg === 'boolean'
        ? devStateLab.state.signup.hasExistingOrg
        : (clerkUser?.organizationMemberships?.length ?? 0) > 0;
    const isCreatingAdditionalOrganization =
      hasExistingOrgForSignupRoute && isCreateOrganizationIntent(location.search);
    const shouldShowDashboardBottomBar =
      !isAuthRoute(location.pathname) && !location.pathname.includes('/onboarding');
    const dashboardBottomBarLeadingSlot = isDevStateLabEnabled() ? (
        <div className="flex min-w-0 items-center gap-2">
          {isDevStateLabEnabled() ? (
            <DevStateLab
              rawMerchant={rawMerchantForDevStateLab}
              effectiveMerchant={merchant}
              state={devStateLab.state}
              setState={devStateLab.setState}
              clearState={devStateLab.clearState}
              activeOrgId={activeOrgId}
              storageKey={devStateLab.storageKey}
              readOnly={readOnly}
              rawReadOnly={user?.attributes?.['custom:read_only'] === 'true'}
            />
          ) : null}
        </div>
      ) : null;

    // React Router v5 reuses a matched <Route> when sibling routes render the
    // same workspace component. Keying those workspaces by their panel-scoped
    // pathname prevents an edit record's local form state from surviving an
    // edit -> create navigation in the same dashboard tab.
    renderDashboardRoutesRef.current = (switchLocation = location) => (
      <Switch location={switchLocation}>
        {/* Paid signup checkout must finish before dashboard navigation. */}
        {isActivationMode && !stripeEntry && (
          <Route path={ACTIVATION_NAV_BLOCK_PATH}>
            <Redirect to="/" />
          </Route>
        )}

        {/* Clerk Sign In Route - Only render when Clerk auth is enabled */}
        <Route path="/signin">
          <SignIn />
        </Route>

        {/* Clerk Sign Up Route - Only render when Clerk auth is enabled */}
        <Route path="/signup">
          <SignUp />
        </Route>

        {/* M2 onboarding: org setup wizard (full-screen, no sidebar).
            Guard: if user already has an org, they've completed onboarding signup —
            redirect to dashboard where activation flow resumes if needed. */}
        <Route path="/onboarding">
          {hasExistingOrgForSignupRoute && !isCreatingAdditionalOrganization ? (
            <Redirect to="/" />
          ) : (
            <AuthMarketingSplitLayout leftColumnClassName="flex flex-1 flex-col min-h-screen overflow-y-auto px-6 py-12 sm:px-6 lg:px-8">
              <SignupFlow
                getAuthToken={getAuthToken}
                clerkUser={clerkUser}
                flowMode={isCreatingAdditionalOrganization ? 'create-organization' : 'initial'}
              />
            </AuthMarketingSplitLayout>
          )}
        </Route>

        <Route exact path="/">
          <Home
            jwtToken={jwtToken}
            setJWTToken={setJWTToken}
            selectableFunds={selectableFunds}
            setSelectableFunds={setSelectableFunds}
            merchant={merchant}
            setMerchant={updateMerchant}
            selectableForms={selectableForms}
            setSelectableForms={setSelectableForms}
            groups={groups}
            user={user}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            stripeConnectInstance={stripeConnectInstance}
          />
        </Route>
        {/* PocketFlows Marketing — flag-gated. Each page self-gates via
            useMarketingTierEnabled and returns null when the gate isn't
            'enabled'. In prod (flag OFF) direct navigation renders nothing
            (effectively a 404). Showing mock data until M-1.1 Stage 2 wires
            marketingTrpc. */}
        {/* Legacy redirect: marketing surfaces moved from /marketing/* to
            top-level routes (/broadcasts, /segments, /email-blasts,
            /email-templates) on 2026-06-12. Non-exact route catches every
            old bookmark/deep-link and strips the prefix, preserving
            subpaths (/marketing/segments/:id/edit → /segments/:id/edit)
            and the query string. Bare /marketing lands on /broadcasts. */}
        <Route
          path="/marketing"
          render={({ location }) => {
            const stripped = location.pathname.replace(/^\/marketing\/?/, '/');
            const target = stripped === '/' ? '/broadcasts' : stripped;
            return <Redirect to={`${target}${location.search}`} />;
          }}
        />
        {/* Marketing routes are entitlement-gated (Base+) with failClosed: a
            STARTER / degraded org gets the full-page UpgradeLock, never the
            tool. Mirrors the hidden sidebar section (Sidebar.js). */}
        {/* Marketing LIST routes are not entitlement-gated at the route level
            (PE-1651): the page renders for everyone and its in-page
            <MarketingPaywall> shows the engagement/upsell to non-entitled orgs
            (and still blocks the real tool). Sub-routes below keep the gate. */}
        <Route exact path="/broadcasts">
          <BroadcastsPage
            merchant={merchant}
            setMerchant={updateMerchant}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            selectableFunds={selectableFunds}
            selectableForms={selectableForms}
          />
        </Route>
        {/* Create-broadcast flow — MUST come before /broadcasts/:id
            since `:id` is a wildcard that would otherwise capture `new`. */}
        <Route exact path="/broadcasts/new">
          <EntitlementGate
            anyOf={MARKETING_FEATURE_KEYS}
            failClosed
            merchant={merchant}
            redirectTo="/broadcasts"
          >
            <NewBroadcastPage
              merchant={merchant}
              setMerchant={updateMerchant}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              selectableFunds={selectableFunds}
              selectableForms={selectableForms}
            />
          </EntitlementGate>
        </Route>
        <Route exact path="/broadcasts/new/:type">
          <EntitlementGate
            anyOf={MARKETING_FEATURE_KEYS}
            failClosed
            merchant={merchant}
            redirectTo="/broadcasts"
          >
            <NewBroadcastPage
              merchant={merchant}
              setMerchant={updateMerchant}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              selectableFunds={selectableFunds}
              selectableForms={selectableForms}
            />
          </EntitlementGate>
        </Route>
        {/* Embed builder for a chosen type — one level deeper than the
            "create" page (scratch + templates) at /broadcasts/new/:type. */}
        <Route exact path="/broadcasts/new/:type/:mode">
          <EntitlementGate
            anyOf={MARKETING_FEATURE_KEYS}
            failClosed
            merchant={merchant}
            redirectTo="/broadcasts"
          >
            <NewBroadcastPage
              merchant={merchant}
              setMerchant={updateMerchant}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              selectableFunds={selectableFunds}
              selectableForms={selectableForms}
            />
          </EntitlementGate>
        </Route>
        {/* Read-only details/summary for a finished campaign — MUST come
            before /broadcasts/:id so the `/details` suffix isn't swallowed
            by the single-segment :id route. Reached from the row menu's
            "View Details" on finished campaigns (BroadcastsColumns). */}
        <Route exact path="/broadcasts/:id/details">
          <EntitlementGate anyOf={MARKETING_FEATURE_KEYS} failClosed merchant={merchant}>
            <CampaignDetailsPage
              merchant={merchant}
              setMerchant={updateMerchant}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              selectableFunds={selectableFunds}
              selectableForms={selectableForms}
            />
          </EntitlementGate>
        </Route>
        {/* Payment Impact drilldown (PE-1607) — broadcast → attributed
            payments. Before /broadcasts/:id so the wildcard doesn't capture. */}
        <Route exact path="/broadcasts/:id/payments">
          {/* PE-1993: NO outer EntitlementGate — BroadcastPaymentsPage wraps its
              own content in MarketingPaywall, which already fails closed to the
              upsell for never-entitled orgs. The outer gate additionally blocked
              READ_ONLY (downgraded) orgs, so "View Payments" on a broadcast they
              still own dead-ended at "Available on the Base plan". Attributed
              payments are our own data and read-only promises access to it. This
              matches /broadcasts and /segments, which are likewise ungated here
              and self-gate in the page. */}
          <BroadcastPaymentsPage
            merchant={merchant}
            setMerchant={updateMerchant}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            selectableFunds={selectableFunds}
            selectableForms={selectableForms}
          />
        </Route>
        {/* Legacy URL redirect — the drilldown moved from /donations to
            /payments (PE-1670). Keep bookmarks/links working. Must stay before
            /broadcasts/:id so the wildcard doesn't capture the suffix. */}
        <Route exact path="/broadcasts/:id/donations">
          {({ match }) => <Redirect to={`/broadcasts/${match.params.id}/payments`} />}
        </Route>
        {/* Trigger edit deep-link — same detail page, embed lands on PF's
            edit screen (/triggers/:pf_id/edit). Before /broadcasts/:id so the
            wildcard doesn't capture the suffix. */}
        <Route exact path="/broadcasts/:id/edit">
          <EntitlementGate anyOf={MARKETING_FEATURE_KEYS} failClosed merchant={merchant}>
            <BroadcastDetailPage
              merchant={merchant}
              setMerchant={updateMerchant}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              selectableFunds={selectableFunds}
              selectableForms={selectableForms}
              editMode
            />
          </EntitlementGate>
        </Route>
        <Route exact path="/broadcasts/:id">
          <EntitlementGate anyOf={MARKETING_FEATURE_KEYS} failClosed merchant={merchant}>
            <BroadcastDetailPage
              merchant={merchant}
              setMerchant={updateMerchant}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              selectableFunds={selectableFunds}
              selectableForms={selectableForms}
            />
          </EntitlementGate>
        </Route>
        {/* Create-segment flow — single embed page (no type chooser since
            segment subtypes aren't a user-facing distinction). */}
        <Route exact path="/segments/new">
          <EntitlementGate
            anyOf={MARKETING_FEATURE_KEYS}
            failClosed
            merchant={merchant}
            redirectTo="/segments"
          >
            <NewSegmentPage
              merchant={merchant}
              setMerchant={updateMerchant}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              selectableFunds={selectableFunds}
              selectableForms={selectableForms}
            />
          </EntitlementGate>
        </Route>
        {/* Segment edit — PF embed editor deep link. Placed before the
            bare list route to keep the create/edit-before-list ordering
            convention of the broadcasts block (all are exact matches). */}
        <Route exact path="/segments/:id/edit">
          <EntitlementGate anyOf={MARKETING_FEATURE_KEYS} failClosed merchant={merchant}>
            <SegmentEditPage
              mode="edit"
              merchant={merchant}
              setMerchant={updateMerchant}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              selectableFunds={selectableFunds}
              selectableForms={selectableForms}
            />
          </EntitlementGate>
        </Route>
        <Route exact path="/segments/:id">
          <EntitlementGate anyOf={MARKETING_FEATURE_KEYS} failClosed merchant={merchant}>
            <SegmentEditPage
              mode="view"
              merchant={merchant}
              setMerchant={updateMerchant}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              selectableFunds={selectableFunds}
              selectableForms={selectableForms}
            />
          </EntitlementGate>
        </Route>
        <Route exact path="/segments">
          <SegmentsPage
            merchant={merchant}
            setMerchant={updateMerchant}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            selectableFunds={selectableFunds}
            selectableForms={selectableForms}
          />
        </Route>
        {/* Brand Kit — PocketFlows brand-kit editor, relocated from Settings →
            Marketing to a first-class Marketing sidebar surface (PE-1670).
            NOT route-gated (matches /broadcasts and /segments): BrandKitPage
            renders its own Header (top bar) and gates ONLY the content area via
            its internal <MarketingPaywall>, which shows the engagement upsell to
            non-entitled orgs INSIDE the page chrome. A route-level EntitlementGate
            here previously pre-empted BrandKitPage before it could mount, so the
            top bar disappeared on the locked/upsell state. */}
        <Route exact path="/brand-kit">
          <BrandKitPage
            merchant={merchant}
            setMerchant={updateMerchant}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            selectableFunds={selectableFunds}
            selectableForms={selectableForms}
          />
        </Route>
        {/* Email Blasts + Email Templates routes removed (PE-1671). */}

        {/* Payments - requires VIEW_PAYMENTS permission */}
        <Route exact path="/payments/new/:paymentType?">
          <PermissionGate permission={P.PROCESS_PAYMENTS}>
            <PaymentWorkspace
              key={switchLocation.pathname}
              jwtToken={jwtToken}
              setJWTToken={setJWTToken}
              selectableFunds={selectableFunds}
              setSelectableFunds={setSelectableFunds}
              merchant={merchant}
              setMerchant={updateMerchant}
              selectableForms={selectableForms}
              setSelectableForms={setSelectableForms}
              groups={groups}
              user={user}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </PermissionGate>
        </Route>

        <Route exact path="/payments/:paymentId/edit">
          <PermissionGate permission={P.MANAGE_PAYMENTS}>
            <PaymentWorkspace
              key={switchLocation.pathname}
              jwtToken={jwtToken}
              setJWTToken={setJWTToken}
              selectableFunds={selectableFunds}
              setSelectableFunds={setSelectableFunds}
              merchant={merchant}
              setMerchant={updateMerchant}
              selectableForms={selectableForms}
              setSelectableForms={setSelectableForms}
              groups={groups}
              user={user}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </PermissionGate>
        </Route>

        <Route exact path="/payments/:paymentId">
          <PermissionGate permission={P.VIEW_PAYMENTS}>
            <PaymentWorkspace
              key={switchLocation.pathname}
              jwtToken={jwtToken}
              setJWTToken={setJWTToken}
              selectableFunds={selectableFunds}
              setSelectableFunds={setSelectableFunds}
              merchant={merchant}
              setMerchant={updateMerchant}
              selectableForms={selectableForms}
              setSelectableForms={setSelectableForms}
              groups={groups}
              user={user}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </PermissionGate>
        </Route>

        <Route exact path="/payments">
          {({ match }) => {
            return (
              <PermissionGate permission={P.VIEW_PAYMENTS}>
                <DonationsWrapper
                  jwtToken={jwtToken}
                  setJWTToken={setJWTToken}
                  selectableFunds={selectableFunds}
                  setSelectableFunds={setSelectableFunds}
                  merchant={merchant}
                  setMerchant={updateMerchant}
                  selectableForms={selectableForms}
                  setSelectableForms={setSelectableForms}
                  groups={groups}
                  user={user}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                />
              </PermissionGate>
            );
          }}
        </Route>

        <Route exact path="/contacts/new">
          <PermissionGate permission={P.PROCESS_CONTACTS}>
            <ContactWorkspace
              key={switchLocation.pathname}
              jwtToken={jwtToken}
              setJWTToken={setJWTToken}
              selectableFunds={selectableFunds}
              setSelectableFunds={setSelectableFunds}
              merchant={merchant}
              setMerchant={updateMerchant}
              selectableForms={selectableForms}
              setSelectableForms={setSelectableForms}
              groups={groups}
              user={user}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </PermissionGate>
        </Route>

        <Route exact path="/contacts/:contactId/edit">
          <PermissionGate permission={P.MANAGE_CONTACTS}>
            <ContactWorkspace
              key={switchLocation.pathname}
              jwtToken={jwtToken}
              setJWTToken={setJWTToken}
              selectableFunds={selectableFunds}
              setSelectableFunds={setSelectableFunds}
              merchant={merchant}
              setMerchant={updateMerchant}
              selectableForms={selectableForms}
              setSelectableForms={setSelectableForms}
              groups={groups}
              user={user}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </PermissionGate>
        </Route>

        <Route exact path="/contacts/:contactId">
          <PermissionGate permission={P.VIEW_CONTACTS}>
            <ContactWorkspace
              key={switchLocation.pathname}
              jwtToken={jwtToken}
              setJWTToken={setJWTToken}
              selectableFunds={selectableFunds}
              setSelectableFunds={setSelectableFunds}
              merchant={merchant}
              setMerchant={updateMerchant}
              selectableForms={selectableForms}
              setSelectableForms={setSelectableForms}
              groups={groups}
              user={user}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </PermissionGate>
        </Route>

        <Route exact path="/contacts">
          {({ match }) => (
            <PermissionGate permission={P.VIEW_CONTACTS}>
              <ContactsWrapper
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
            </PermissionGate>
          )}
        </Route>

        <Route exact path="/subscriptions/:subscriptionId/edit">
          <PermissionGate permission={P.MANAGE_SUBSCRIPTIONS}>
            <SubscriptionWorkspace
              key={switchLocation.pathname}
              jwtToken={jwtToken}
              setJWTToken={setJWTToken}
              selectableFunds={selectableFunds}
              setSelectableFunds={setSelectableFunds}
              merchant={merchant}
              setMerchant={updateMerchant}
              selectableForms={selectableForms}
              setSelectableForms={setSelectableForms}
              groups={groups}
              user={user}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </PermissionGate>
        </Route>

        <Route exact path="/subscriptions/:subscriptionId">
          <PermissionGate permission={P.VIEW_SUBSCRIPTIONS}>
            <SubscriptionWorkspace
              key={switchLocation.pathname}
              jwtToken={jwtToken}
              setJWTToken={setJWTToken}
              selectableFunds={selectableFunds}
              setSelectableFunds={setSelectableFunds}
              merchant={merchant}
              setMerchant={updateMerchant}
              selectableForms={selectableForms}
              setSelectableForms={setSelectableForms}
              groups={groups}
              user={user}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </PermissionGate>
        </Route>

        <Route exact path="/subscriptions">
          {({ match }) => (
            <PermissionGate permission={P.VIEW_SUBSCRIPTIONS}>
              <SubscriptionsWrapper
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
            </PermissionGate>
          )}
        </Route>

        <Route exact path="/forms/new/:formType?">
          <PermissionGate permission={P.PROCESS_FORMS}>
            <FormWorkspace key={switchLocation.pathname} {...formWorkspaceProps} />
          </PermissionGate>
        </Route>

        <Route exact path="/forms/:formId/submissions/:submissionId">
          <PermissionGate permission={P.VIEW_FORMS}>
            <PermissionGate permission={P.VIEW_PAYMENTS}>
              <FormSubmissionWorkspace key={switchLocation.pathname} {...formWorkspaceProps} />
            </PermissionGate>
          </PermissionGate>
        </Route>

        <Route exact path="/forms/:formId/edit">
          <PermissionGate permission={P.MANAGE_FORMS}>
            <FormWorkspace key={switchLocation.pathname} {...formWorkspaceProps} />
          </PermissionGate>
        </Route>

        <Route path="/forms/:id?">
          {({ match }) => (
            <PermissionGate permission={P.VIEW_FORMS}>
              <FormsWrapper
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
            </PermissionGate>
          )}
        </Route>

        <Route exact path="/elements/new/:elementType?">
          <PermissionGate permission={P.PROCESS_ELEMENTS}>
            <EntitlementGate
              anyOf={ELEMENT_FEATURE_KEYS}
              hydrateMerchant={updateMerchant}
              redirectTo="/elements"
            >
              <ElementWorkspace
                key={switchLocation.pathname}
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
            </EntitlementGate>
          </PermissionGate>
        </Route>

        <Route exact path="/elements/:elementId/edit">
          <PermissionGate permission={P.PROCESS_ELEMENTS}>
            <EntitlementGate anyOf={ELEMENT_FEATURE_KEYS} hydrateMerchant={updateMerchant}>
              <ElementWorkspace
                key={switchLocation.pathname}
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
            </EntitlementGate>
          </PermissionGate>
        </Route>

        {/* Elements LIST route is NOT route-gated (matches /broadcasts, /segments,
            /brand-kit): the Elements page renders its own Header (top bar) and
            gates ONLY the content area — showing the ElementsEngagement upsell to
            non-entitled orgs INSIDE the page chrome (see Elements.js). A route-level
            EntitlementGate here previously replaced the whole page with the upsell,
            dropping the top bar. The deeper builder routes (/elements/new,
            /elements/:id/edit) KEEP their EntitlementGate — ElementWorkspace must
            never mount for a non-entitled org (it doesn't self-fetch the merchant). */}
        <Route path="/elements/:id?">
          {({ match }) => {
            return (
              <PermissionGate permission={P.VIEW_ELEMENTS}>
                <ElementsWrapper
                  jwtToken={jwtToken}
                  setJWTToken={setJWTToken}
                  selectableFunds={selectableFunds}
                  setSelectableFunds={setSelectableFunds}
                  merchant={merchant}
                  setMerchant={updateMerchant}
                  selectableForms={selectableForms}
                  setSelectableForms={setSelectableForms}
                  groups={groups}
                  user={user}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                />
              </PermissionGate>
            );
          }}
        </Route>

        <Route exact path="/payouts/:id?">
          {({ match }) => (
            <PermissionGate permission={P.VIEW_PAYOUTS}>
              <PayoutsWrapper
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
            </PermissionGate>
          )}
        </Route>

        {/* Conditional routes */}
        {readOnly === true ? (
          <Switch>
            {/* <Route exact path="/funds">
              <Funds
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
              />
            </Route> */}

            <Route exact path="/comingsoon">
              <ComingSoon
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                merchant={merchant}
              />
            </Route>

            <Route exact path="/contacts">
              <PermissionGate permission={P.VIEW_CONTACTS}>
                <Donors
                  jwtToken={jwtToken}
                  setJWTToken={setJWTToken}
                  selectableFunds={selectableFunds}
                  setSelectableFunds={setSelectableFunds}
                  merchant={merchant}
                  setMerchant={updateMerchant}
                  selectableForms={selectableForms}
                  setSelectableForms={setSelectableForms}
                  groups={groups}
                  user={user}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                />
              </PermissionGate>
            </Route>
            <Route exact path="/payouts">
              <PermissionGate permission={P.VIEW_PAYOUTS}>
                <Payouts
                  jwtToken={jwtToken}
                  setJWTToken={setJWTToken}
                  selectableFunds={selectableFunds}
                  setSelectableFunds={setSelectableFunds}
                  merchant={merchant}
                  setMerchant={updateMerchant}
                  selectableForms={selectableForms}
                  setSelectableForms={setSelectableForms}
                  groups={groups}
                  user={user}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                />
              </PermissionGate>
            </Route>

            <Route exact path={['/receivables']}>
              <OptIn
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
            </Route>

            {/* <Route exact path="/tickets">
              <Tickets
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
              />
            </Route> */}
            <Route>
              <NotFound />
            </Route>
          </Switch>
        ) : (
          <Switch>
            <Route exact path="/Payments">
              <PermissionGate permission={P.VIEW_PAYMENTS}>
                <DonationsWrapper
                  jwtToken={jwtToken}
                  setJWTToken={setJWTToken}
                  selectableFunds={selectableFunds}
                  setSelectableFunds={setSelectableFunds}
                  merchant={merchant}
                  setMerchant={updateMerchant}
                  selectableForms={selectableForms}
                  setSelectableForms={setSelectableForms}
                  groups={groups}
                  user={user}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                />
              </PermissionGate>
            </Route>

            <Route exact path="/dashboard">
              <Redirect to="/" />
            </Route>
            {/* <Route exact path="/funds">
              <Funds
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
              />
            </Route> */}

            <Route exact path="/forms">
              <PermissionGate permission={P.VIEW_FORMS}>
                <Fundraisers
                  jwtToken={jwtToken}
                  setJWTToken={setJWTToken}
                  selectableFunds={selectableFunds}
                  setSelectableFunds={setSelectableFunds}
                  merchant={merchant}
                  setMerchant={updateMerchant}
                  selectableForms={selectableForms}
                  setSelectableForms={setSelectableForms}
                  groups={groups}
                  user={user}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                />
              </PermissionGate>
            </Route>

            {/* Billing moved to a top-level Settings tab (/settings/billing).
                Redirect the legacy sub-tab URL — still targeted by the Stripe
                checkout return URL and older deep links — preserving the query
                string so the ?checkout=success|cancelled handoff survives. Must
                precede the settings catch-all Route below. */}
            <Route
              exact
              path="/settings/organization/billing"
              render={({ location }) => (
                <Redirect to={{ pathname: '/settings/billing', search: location.search }} />
              )}
            />

            {/* Tag management moved from Settings → Organization → Tags to the
                Contacts page (Manage Tags modal). Redirect the retired sub-tab
                URL — still targeted by the marketing tour + older deep links.
                Must precede the settings catch-all Route below. */}
            <Route exact path="/settings/organization/tags">
              <Redirect to="/contacts" />
            </Route>

            {/* The import WIZARD (PE-1905/1906, SPEC §4). MUST be declared
                before the `/settings/:settingsTab?/:settingsSection?` catch-all
                below, or that route swallows it and renders Settings instead.

                NOT a Settings tab, deliberately. PE-1910 moved the Import HUB
                into the Settings shell (SettingsContent.js renders it as the
                `imports` tab), but the wizard is an entity WORKSPACE: it
                renders through DashboardEntityWorkspace, so it gets its own
                dashboard tab, a Settings > Imports > [file] breadcrumb, and
                the shared Header — exactly like ContactWorkspace. Hub = read
                surface in Settings; wizard = entity workspace. Two shells on
                purpose. "Start new import" opens it via
                openDashboardPathInNewTab so it never replaces the Settings tab
                the admin launched it from.

                Gated on IMPORT_CONTACTS, not VIEW_CONTACTS: this route WRITES.
                (The Hub is a read surface and gates on VIEW_CONTACTS.) The
                backend re-checks the permission on every call regardless — UI
                gating is UX only, per SPEC §2.5.

                The `isAdmin` WIP kill switch that used to wrap this route was
                removed by PE-1915 (Milestone F): "nothing is user-reachable
                until Milestone F's entry points land" is what made riding
                half-built import milestones to dev safe, and F landing is the
                deliberate un-gating step, not a cleanup. IMPORT_CONTACTS is the
                real, permanent gate — it stays. */}
            {/* IMPORT_CONTACTS is enforced in ImportWizardRoute (see its
                definition above). The PE-1584 PostHog gate that used to wrap
                this route was removed at full rollout. */}
            <Route exact path="/settings/imports/new">
              <ImportWizardRoute
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                selectableFunds={selectableFunds}
                selectableForms={selectableForms}
                merchant={merchant}
              />
            </Route>

            {/* Nested Switch selects the import wizard before the Settings shell. */}
            <Route path="/settings/:settingsTab?/:settingsSection?">
              {({ match }) =>
                !match ? null : (
                  <Settings
                    jwtToken={jwtToken}
                    setJWTToken={setJWTToken}
                    selectableFunds={selectableFunds}
                    setSelectableFunds={setSelectableFunds}
                    merchant={merchant}
                    setMerchant={updateMerchant}
                    selectableForms={selectableForms}
                    setSelectableForms={setSelectableForms}
                    groups={groups}
                    user={user}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    stripeConnectInstance={stripeConnectInstance}
                    stripeConnectError={stripeConnectError}
                    isAdmin={effectiveIsAdmin}
                  />
                )
              }
            </Route>

            <Route exact path="/contacts">
              <PermissionGate permission={P.VIEW_CONTACTS}>
                <Donors
                  jwtToken={jwtToken}
                  setJWTToken={setJWTToken}
                  selectableFunds={selectableFunds}
                  setSelectableFunds={setSelectableFunds}
                  merchant={merchant}
                  setMerchant={updateMerchant}
                  selectableForms={selectableForms}
                  setSelectableForms={setSelectableForms}
                  groups={groups}
                  user={user}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                />
              </PermissionGate>
            </Route>

            <Route exact path="/subscriptions">
              <PermissionGate permission={P.VIEW_SUBSCRIPTIONS}>
                <Subscriptions
                  jwtToken={jwtToken}
                  setJWTToken={setJWTToken}
                  selectableFunds={selectableFunds}
                  setSelectableFunds={setSelectableFunds}
                  merchant={merchant}
                  setMerchant={updateMerchant}
                  selectableForms={selectableForms}
                  setSelectableForms={setSelectableForms}
                  groups={groups}
                  user={user}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                />
              </PermissionGate>
            </Route>

            <Route exact path="/payouts">
              <PermissionGate permission={P.VIEW_PAYOUTS}>
                <Payouts
                  jwtToken={jwtToken}
                  setJWTToken={setJWTToken}
                  selectableFunds={selectableFunds}
                  setSelectableFunds={setSelectableFunds}
                  merchant={merchant}
                  setMerchant={updateMerchant}
                  selectableForms={selectableForms}
                  setSelectableForms={setSelectableForms}
                  groups={groups}
                  user={user}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                />
              </PermissionGate>
            </Route>

            {/* <Route exact path="/events">
              <Events
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
              />
            </Route> */}
            {/* <Route exact path="/tickets">
              <Tickets
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
              />
            </Route> */}
            {/* if the path is optin, elements, receivables, or subscriptions, render the OptIn component */}
            <Route exact path={['/optin', '/receivables', '/subscriptions']}>
              <OptIn
                jwtToken={jwtToken}
                setJWTToken={setJWTToken}
                selectableFunds={selectableFunds}
                setSelectableFunds={setSelectableFunds}
                merchant={merchant}
                setMerchant={updateMerchant}
                selectableForms={selectableForms}
                setSelectableForms={setSelectableForms}
                groups={groups}
                user={user}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
            </Route>
            <Route>
              <NotFound />
            </Route>
          </Switch>
        )}

        {/* Catch-all route for undefined paths - MUST BE LAST */}
        <Route path="*">
          <NotFound />
        </Route>
      </Switch>
    );

    const layout = (
      <SetupChecklistPreferencesProvider
        userId={clerkUser?.id}
        organizationId={activeOrgId}
        scope={clerkUser?.id && activeOrgId ? `${clerkUser.id}:${activeOrgId}` : null}
        legacyScope={clerkUser?.id && paymentOrganizationKey(rawMerchantForDevStateLab) ? `${clerkUser.id}:${paymentOrganizationKey(rawMerchantForDevStateLab)}` : null}
        ready={Boolean(rawMerchantForDevStateLab && !rawMerchantForDevStateLab.__charitystackRouterSeed)}
      >
      <OnboardingDriverProvider
        isActivationMode={isActivationMode}
        sidebarOpen={sidebarOpen}
        merchant={effectiveMerchant}
        setMerchant={updateMerchant}
      >
          <div className="flex flex-col h-dvh bg-paper-muted">
            <motion.div
              initial={false}
              animate={{
                '--dashboard-assistant-dock-width':
                  DASHBOARD_ASSISTANT_ENABLED &&
                  shouldShowDashboardBottomBar &&
                  isAssistantDocked &&
                  isAssistantExpanded
                    ? 400
                    : 0,
              }}
              transition={shouldReduceAssistantMotion ? { duration: 0 } : chatPanelLayoutTransition}
              className="flex min-h-0 flex-1 overflow-clip lg:pr-[calc(var(--dashboard-assistant-dock-width)*1px)]"
              data-assistant-shell
              data-mobile-menu-open={sidebarOpen}
            >
              {/* Sidebar - hidden on auth pages and on M2 onboarding (full-screen wizard) */}
              {!isAuthRoute(location.pathname) && !location.pathname.includes('/onboarding') && (
                <Sidebar
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                  groups={groups}
                  merchant={merchant}
                  selectableFunds={selectableFunds}
                  selectableForms={selectableForms}
                />
              )}

              {/* Dashboard content and optional payment setup. */}
              <DashboardOnboardingChrome
                merchantHydrationEnabled={merchantHydrationEnabled}
                merchantHydrationStatus={merchantHydrationStatus}
                onRetryMerchant={() => setMerchantRefreshKey((version) => version + 1)}
                merchant={effectiveMerchant}
                setMerchant={updateMerchant}
                stripeConnectInstance={stripeConnectInstance}
                clerkUser={clerkUser}
                isActivationMode={isActivationMode}
                hasDashboardBottomBar={shouldShowDashboardBottomBar}
              >
                <DashboardRouteCache
                  activeLocation={location}
                  renderRoutes={renderDashboardRoutes}
                  cacheScope={orgRouteCacheScope}
                  fallbackHeaderProps={{
                    sidebarOpen,
                    setSidebarOpen,
                    selectableFunds,
                    selectableForms,
                    merchant,
                  }}
                />
              </DashboardOnboardingChrome>
            </motion.div>
            {shouldShowDashboardBottomBar ? (
              <>
                {DASHBOARD_ASSISTANT_ENABLED ? (
                  <DashboardAssistantRuntime
                    dashboardTimezone={merchant?.timezone}
                    organizationNames={groups}
                  />
                ) : null}
                <DashboardAssistantBottomBar
                  dashboardTimezone={merchant?.timezone}
                  merchantCountryCode={merchant?.countryCode}
                  leadingSlot={dashboardBottomBarLeadingSlot}
                  sidebarOpen={sidebarOpen}
                  onToggleSidebar={() => setSidebarOpen((open) => !open)}
                  onCloseSidebar={() => setSidebarOpen(false)}
                  onOpenGettingStarted={() => setSidebarOpen(true)}
                />
              </>
            ) : null}
          </div>
          {/* close flex flex-col h-screen wrapper */}
      </OnboardingDriverProvider>
      </SetupChecklistPreferencesProvider>
    );

    return (
      // App-wide entitlement gating (PE-1624): resolve once per
      // merchant.billingDetails and expose can()/limit() to the Sidebar and every
      // route below. The Billing & Plans page (PE-1572) mounts its own provider
      // locally; nesting is harmless (same merchant ⇒ same resolved value).
      <EntitlementsProvider merchant={merchant}>
        <StripeSessionProvider session={stripeSession}>{layout}</StripeSessionProvider>
      </EntitlementsProvider>
    );
  }

  return <Loading />; // or a loading indicator
}

// Clerk authentication: Wrap App with ClerkAuthWrapper (ClerkProvider is in index.jsx)
const AppWithAuth = () => (
  <ClerkAuthWrapper>
    {({ signOut, user, isAdmin, clerkUser, activeOrgId }) => (
      <App
        signOut={signOut}
        user={user}
        isAdmin={isAdmin}
        clerkUser={clerkUser}
        activeOrgId={activeOrgId}
      />
    )}
  </ClerkAuthWrapper>
);
AppWithAuth.displayName = 'AppWithAuth';

export default AppWithAuth;
